import { ApplicationRef, ComponentRef, createComponent, EmbeddedViewRef, Injectable, ViewContainerRef } from "@angular/core";
import { SignDragResizeComponent } from "@components/sign-drag-resize/sign-drag-resize.component";
import { Constants } from "@config/constants";
import { IGetSignerSignature, ISignUploadSignature } from "@modals/api.modal";
import * as htmlToImage from "html-to-image";
import { HttpService } from "./http.service";
import { EnumSignerRoleType, EnumSignTagTypes, ISignTag } from "@modals/app.modal";
import { SignTagElementComponent } from "@components/sign-tag-element/sign-tag-element.component";
import {
  DEFAULT_SELF_SIGN_SIGNATURE_COLOR,
  stripSignatureBackgroundFromBase64,
} from "@utils/signature-color.util";


export interface IPlacedSignatureRecord {
  imageBase64: string;
  isCommentOnly?: boolean;
  signComment?: string;
  comment?: string;
  signatureId?: string;
  isResponsiveContainer?: boolean;
  isDraggable?: boolean;
  disableDelete?: boolean;
  pageIndex: number;

  submitSnapshot?: Omit<ISignUploadSignature, 'pageIndex'>;
 
  hostGeometry?: {
    left: string;
    top: string;
    width: string;
    height: string;
    className: string;
    page: string;
  };
}

@Injectable({
  providedIn: "root",
})
export class SignService {
  public isLoadingSignerSignatures: boolean = false;
  public signerSignatures: Array<IGetSignerSignature> = [];
  public signTags: Array<ISignTag> = [];
  public roleText: string = 'Signer';
  public allTagsCount: number = 0;

  /** Normalize API / editor tag type strings to `EnumSignTagTypes` values. */
  static normalizeSignTagType(type: unknown): EnumSignTagTypes {
    const raw = String(type ?? '').trim();
    if (!raw) return EnumSignTagTypes.TEXT;
    const lower = raw.toLowerCase();
    const match = (Object.values(EnumSignTagTypes) as string[]).find(
      (v) => v.toLowerCase() === lower,
    );
    if (match) return match as EnumSignTagTypes;
    if (lower === 'esign' || lower === 'signature') return EnumSignTagTypes.SIGNATURE;
    return raw as EnumSignTagTypes;
  }

  static normalizeSignTag(raw: Record<string, unknown>): ISignTag {
    return {
      Height: Number(raw['Height'] ?? raw['height'] ?? 0),
      Page: Number(raw['Page'] ?? raw['page'] ?? 1),
      Text: String(raw['Text'] ?? raw['text'] ?? ''),
      Type: SignService.normalizeSignTagType(raw['Type'] ?? raw['type']),
      Width: Number(raw['Width'] ?? raw['width'] ?? 0),
      XLocation: Number(raw['XLocation'] ?? raw['xLocation'] ?? raw['Xlocation'] ?? 0),
      YLocation: Number(raw['YLocation'] ?? raw['yLocation'] ?? raw['Ylocation'] ?? 0),
      IsRendered: raw['IsRendered'] as boolean | undefined,
    };
  }

  static parseSignTagsFromApi(tags: string | null | undefined): ISignTag[] {
    if (!tags || !String(tags).trim()) return [];
    try {
      const parsed = JSON.parse(`[${tags}]`) as Record<string, unknown>[];
      if (!Array.isArray(parsed)) return [];
      return parsed.map((t) => SignService.normalizeSignTag(t));
    } catch {
      return [];
    }
  }


  private _placedSignatures: Map<string, IPlacedSignatureRecord> = new Map();
  private _nextDragSuppressHandler:
    | ((suppressed: boolean, restoreDelayMs?: number) => void)
    | null = null;

  registerNextDragSuppressHandler(
    handler: ((suppressed: boolean, restoreDelayMs?: number) => void) | null,
  ): void {
    this._nextDragSuppressHandler = handler;
  }

  notifyTagDragSuppressNext(suppressed: boolean, restoreDelayMs = 0): void {
    this._nextDragSuppressHandler?.(suppressed, restoreDelayMs);
  }

  public customSignTags: Array<{
    label: string;
    icon: string;
    type: EnumSignTagTypes;
    dimensions: {
      width: string;
      height: string;
    }
  }> = [
      {
        label: 'Initials',
        icon: 'short_text',
        type: EnumSignTagTypes.INITIALS,
        dimensions: {
          height: '36px',
          width: '140px'
        }
      },
      {
        label: 'Stamp',
        icon: 'image',
        type: EnumSignTagTypes.STAMP,
        dimensions: {
          height: '60px',
          width: '140px'
        }
      },
      {
        label: 'Text',
        icon: 'text_fields',
        type: EnumSignTagTypes.TEXT,
        dimensions: {
          height: '36px',
          width: '140px'
        }
      },
      {
        label: 'Date',
        icon: 'calendar_today',
        type: EnumSignTagTypes.DATE,
        dimensions: {
          height: '36px',
          width: '165px'
        }
      },
      {
        label: 'Checkbox',
        icon: 'check_box',
        type: EnumSignTagTypes.CHECKBOX,
        dimensions: {
          height: '48px',
          width: '40px'
        }
      }
    ];

  constructor(
    private _httpService: HttpService,
    private _appRef: ApplicationRef) { }

 
  registerPlacedSignature(id: string, record: IPlacedSignatureRecord): void {
    if (!id) return;
    this._placedSignatures.set(id, record);
  }

  updatePlacedSignatureSnapshot(id: string, snapshot: IPlacedSignatureRecord['submitSnapshot']): void {
    if (!id || !snapshot) return;
    const existing = this._placedSignatures.get(id);
    if (existing) {
      existing.submitSnapshot = snapshot;
    }
  }

  updatePlacedSignatureGeometry(id: string, host: HTMLElement | null): void {
    if (!id || !host) return;
    const existing = this._placedSignatures.get(id);
    if (!existing) return;
    const pageEl = host.closest('.page') as HTMLElement | null;
    existing.hostGeometry = {
      left: host.style.left || '',
      top: host.style.top || '',
      width: host.style.width || '',
      height: host.style.height || '',
      className: host.className || '',
      page: pageEl?.getAttribute('data-page-number') ?? String(existing.pageIndex),
    };
  }

 
  async refreshPlacedSignatureSnapshot(
    tagId: string,
    viewContainerRef?: ViewContainerRef,
  ): Promise<void> {
    if (!tagId) return;
    const record = this._placedSignatures.get(tagId);
    if (!record) return;

    const host = document.querySelector(`[data-kzn-tag-id="${tagId}"]`) as HTMLElement | null;
    const cdkDrag =
      (host?.querySelector('.cdk-drag') as HTMLElement | null) ??
      (document.querySelector(`[data-kzn-tag-id="${tagId}"] .cdk-drag`) as HTMLElement | null);
    const pageEl =
      (host?.closest('.page') as HTMLElement | null) ??
      (document.getElementsByClassName('page')[record.pageIndex] as HTMLElement | undefined);

    if (!cdkDrag || !pageEl) return;

    const details = await this.getRequestDetails(cdkDrag, pageEl);
    if (!details?.signImgStr) return;

    this.updatePlacedSignatureSnapshot(tagId, {
      xPercent: details.xPercent,
      yPercent: details.yPercent,
      imgWidthPercent: details.imgWidthPercent,
      imgHeightPercent: details.imgHeightPercent,
      signImgStr: details.signImgStr,
      signComment: details.signComment ?? '',
      signatureId: details.signatureId ?? '',
      isComment: !!record.isCommentOnly,
      commentText: details.commentText ?? '',
    });
  }

 
  restoreAllPlacedSignatures(viewContainerRef: ViewContainerRef): void {
    for (const [tagId, record] of this._placedSignatures.entries()) {
      const host = document.querySelector(`[data-kzn-tag-id="${tagId}"]`) as HTMLElement | null;
      if (host?.isConnected) {
        this._restoreSignatureIfRegistered(host, record.pageIndex, viewContainerRef, tagId);
      }
    }
  }

 
  recreateOrphanedPlacements(viewContainerRef: ViewContainerRef): void {
    if (typeof document === 'undefined' || !viewContainerRef) return;
    const pages = document.getElementsByClassName('page');

    for (const [tagId, record] of this._placedSignatures.entries()) {
      // API (`pt-`) and keyword (`kw-`/`kc-`) hosts are rebuilt elsewhere.
      if (!tagId.startsWith('dp-') && !tagId.startsWith('sh-')) continue;

      const existing = document.querySelector(
        `[data-kzn-tag-id="${tagId}"]`,
      ) as HTMLElement | null;
      if (existing?.isConnected) continue; // host still alive

      const pageEl = pages[record.pageIndex] as HTMLElement | undefined;
      const textLayer = pageEl?.querySelector('.textLayer') as HTMLElement | null;
      if (!textLayer) continue; // page not rendered yet — a later pass will retry

      const geo = record.hostGeometry;
      const snap = record.submitSnapshot;
      const span = document.createElement('span');
      span.setAttribute('data-kzn-tag-id', tagId);
      span.setAttribute('data-drag-placeholder', 'true');
     
      span.setAttribute('data-kzn-signhere-bound', 'true');
      span.style.position = 'absolute';

      if (geo) {
        span.style.left = geo.left;
        span.style.top = geo.top;
        if (geo.width) span.style.width = geo.width;
        if (geo.height) span.style.height = geo.height;
        span.className = geo.className || 'box-sign-here';
        if (geo.page) span.setAttribute('page', geo.page);
      } else if (snap) {
        span.style.left = `${snap.xPercent}%`;
        span.style.top = `${snap.yPercent}%`;
        if (snap.imgWidthPercent) span.style.width = `${snap.imgWidthPercent}%`;
        span.className = record.isCommentOnly
          ? 'box-sign-here sign-tag-comment-container'
          : 'box-sign-here sign-here-standard';
        span.setAttribute('page', record.pageIndex.toString());
      } else {
        continue; // no geometry to place it — cannot safely recreate
      }

      textLayer.appendChild(span);
      this._restoreSignatureIfRegistered(
        span,
        record.pageIndex,
        viewContainerRef,
        tagId,
      );
    }
  }

  countRegistrySignaturePlacements(pageIndex?: number): number {
    let count = 0;
    for (const [, rec] of this._placedSignatures.entries()) {
      if (rec.isCommentOnly) continue;
      if (pageIndex == null || rec.pageIndex === pageIndex) count++;
    }
    return count;
  }

  removePlacedSignature(id: string): void {
    if (!id) return;
    this._placedSignatures.delete(id);
  }

  hasPlacedSignature(id: string): boolean {
    return !!id && this._placedSignatures.has(id);
  }

  getPlacedSignature(id: string): IPlacedSignatureRecord | undefined {
    return this._placedSignatures.get(id);
  }

 
  clearPlacedSignatures(): void {
    this._placedSignatures.clear();
  }


  getPlacedSignaturePageIndexes(): Set<number> {
    const pages = new Set<number>();
    for (const rec of this._placedSignatures.values()) {
      pages.add(rec.pageIndex);
    }
    return pages;
  }

  getPlacedSignatureEntries(): Array<[string, IPlacedSignatureRecord]> {
    return Array.from(this._placedSignatures.entries());
  }

  private _isRealSignatureImage(img: HTMLImageElement | null | undefined): boolean {
    if (!img || img.classList.contains('img-sign-here')) return false;
    const src = (img.currentSrc || img.src || '').trim();
    if (src.includes('sign-here')) return false;
    if (src.startsWith('data:')) {
      const payload = src.includes(',') ? src.split(',')[1] : '';
      return payload.length >= 48;
    }
    if (src.length > 12) return true;
    const signatureId = img.getAttribute('signatureid')?.trim();
    return !!signatureId;
  }

  hostHasPlacedSignatureContent(host: HTMLElement | null | undefined): boolean {
    if (!host) return false;
    const imgs = host.querySelectorAll(
      '.cdk-drag img[data-type="SIGNATURE"], .cdk-drag img[signatureid], .cdk-drag .sign-container img[data-type="SIGNATURE"]',
    );
    for (const node of Array.from(imgs)) {
      if (this._isRealSignatureImage(node as HTMLImageElement)) return true;
    }
    return false;
  }

  hasPlacedSignatureRecordContent(record?: IPlacedSignatureRecord): boolean {
    if (!record || record.isCommentOnly) return false;
    if (record.submitSnapshot?.signImgStr?.trim()) return true;
    if (record.imageBase64?.trim()) return true;
    return false;
  }

  static isSignatureTagType(type: EnumSignTagTypes | string | undefined): boolean {
    return SignService.normalizeSignTagType(type) === EnumSignTagTypes.SIGNATURE;
  }

  collectAllRequiredTagIds(legacyTagIds?: Iterable<string>): string[] {
    const ids = new Set<string>();

    if (this.signTags.length) {
      this.signTags.forEach((tag, index) => {
        if (tag.Type === EnumSignTagTypes.COMMENT) return;
        ids.add(`pt-${index}`);
      });
    }

    if (legacyTagIds) {
      for (const tagId of legacyTagIds) {
        if (!tagId || tagId.startsWith('dp-') || tagId.startsWith('kc-')) continue;
        if (this.signTags.length && tagId.startsWith('pt-')) continue;
        ids.add(tagId);
      }
    }

    if (!ids.size && legacyTagIds) {
      for (const tagId of legacyTagIds) {
        if (!tagId || tagId.startsWith('dp-') || tagId.startsWith('kc-')) continue;
        ids.add(tagId);
      }
    }

    return Array.from(ids);
  }

  private _resolveRequiredTagPageNumber(tagId: string): number {
    const ptMatch = tagId.match(/^pt-(\d+)$/);
    if (ptMatch && this.signTags.length) {
      const tag = this.signTags[parseInt(ptMatch[1], 10)];
      if (tag?.Page) return tag.Page;
    }

    const host = document.querySelector(`[data-kzn-tag-id="${tagId}"]`) as HTMLElement | null;
    const hostPage = host?.getAttribute('page');
    if (hostPage != null && hostPage !== '') {
      return +hostPage + 1;
    }

    const record = this.getPlacedSignature(tagId);
    if (record) return record.pageIndex + 1;

    return 1;
  }

  isSignatureRequiredTagId(tagId: string): boolean {
    const ptMatch = tagId.match(/^pt-(\d+)$/);
    if (ptMatch && this.signTags.length) {
      const tag = this.signTags[parseInt(ptMatch[1], 10)];
      return SignService.isSignatureTagType(tag?.Type);
    }

    const host = document.querySelector(`[data-kzn-tag-id="${tagId}"]`) as HTMLElement | null;
    if (host?.classList.contains('sign-tag-element-container')) return false;
    return !tagId.startsWith('kc-') && !tagId.startsWith('dp-');
  }

  isRequiredTagIdFilled(tagId: string): boolean {
    const ptMatch = tagId.match(/^pt-(\d+)$/);
    if (ptMatch && this.signTags.length) {
      return this.isRequiredSignTagFilled(parseInt(ptMatch[1], 10));
    }

    const host = document.querySelector(`[data-kzn-tag-id="${tagId}"]`) as HTMLElement | null;

    if (host?.classList.contains('sign-tag-element-container')) {
      return (
        !!host.querySelector('[data-value-available="true"]') ||
        !!host.querySelector('.cdk-drag [data-value-available="true"]')
      );
    }

    if (host?.querySelector('img.img-sign-here')) return false;

    const record = this.getPlacedSignature(tagId);
    if (this.hasPlacedSignatureRecordContent(record)) return true;
    if (!host) return false;

    return this.isSignHereHostFilled(host, tagId);
  }

  removeSignHerePlaceholderImages(host: HTMLElement | null | undefined): void {
    if (!host) return;
    host.querySelectorAll('img.img-sign-here').forEach((img) => img.remove());
  }

  clearFilledSignHerePlaceholders(root: ParentNode = document): void {
    root.querySelectorAll('.box-sign-here').forEach((node) => {
      const host = node as HTMLElement;
      const tagId = host.getAttribute('data-kzn-tag-id') || '';
      if (!this.isSignHereHostFilled(host, tagId)) return;
      this.removeSignHerePlaceholderImages(host);
      host.classList.add('sign-here-filled');
    });
  }

  private _isSignerRole(role: EnumSignerRoleType | string | null | undefined): boolean {
    return (role ?? '').toString().trim().toLowerCase() === EnumSignerRoleType.SIGNER.toLowerCase();
  }

  private _hideUnsignedSignHereHost(host: HTMLElement): void {
    host.style.setProperty('display', 'none', 'important');
    host.style.setProperty('visibility', 'hidden', 'important');
    host.style.setProperty('pointer-events', 'none', 'important');
  }

  suppressSignHereForNonSignerRole(role: EnumSignerRoleType | string | null | undefined): void {
    if (this._isSignerRole(role)) return;
    document.querySelectorAll('.box-sign-here.sign img.img-sign-here').forEach((img) => {
      const host = img.closest('.box-sign-here') as HTMLElement | null;
      if (!host || host.classList.contains('sign-tag-comment-container')) return;
      img.remove();
    });
    document
      .querySelectorAll(
        '.box-sign-here.sign:not(.sign-tag-comment-container):not(.comment-placeholder-container)',
      )
      .forEach((node) => {
        const host = node as HTMLElement;
        const tagId = host.getAttribute('data-kzn-tag-id') || '';
        this.removeSignHerePlaceholderImages(host);
        if (this.isSignHereHostFilled(host, tagId)) {
          return;
        }
        this._hideUnsignedSignHereHost(host);
      });
  }

  isSignHereHostFilled(host: HTMLElement | null, tagId?: string): boolean {
    const record = tagId ? this.getPlacedSignature(tagId) : undefined;

    if (!host) {
      return this.hasPlacedSignatureRecordContent(record);
    }

    if (host.querySelector('img.img-sign-here')) {
      return false;
    }

    if (this.hostHasPlacedSignatureContent(host)) {
      return true;
    }

    if (this.hasPlacedSignatureRecordContent(record)) {
      return true;
    }

    if (host.classList.contains('sign-here-filled')) {
      return this.hostHasPlacedSignatureContent(host) || this.hasPlacedSignatureRecordContent(record);
    }

    return false;
  }

  countUnsignedSignHerePlaceholders(root: ParentNode = document): number {
    let count = 0;
    root.querySelectorAll('.box-sign-here.sign img.img-sign-here').forEach((img) => {
      const host = img.closest('.box-sign-here') as HTMLElement | null;
      if (!host) return;
      const tagId = host.getAttribute('data-kzn-tag-id') || '';
      if (tagId && this.hasPlacedSignature(tagId)) return;
      if (this.isSignHereHostFilled(host, tagId)) return;
      count++;
    });
    return count;
  }

  countUnsignedCustomTagFields(root: ParentNode = document): number {
    let count = 0;
    root.querySelectorAll('.sign-tag-element-container').forEach((el) => {
      const host = el as HTMLElement;
      const hasValue =
        !!host.querySelector('[data-value-available="true"]') ||
        !!host.querySelector('.cdk-drag [data-value-available="true"]');
      if (!hasValue) count++;
    });
    return count;
  }

  generateUUID(): string {
    var d = new Date().getTime();
    var d2 =
      (typeof performance !== "undefined" &&
        performance.now &&
        performance.now() * 1000) ||
      0;
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = Math.random() * 16;
        if (d > 0) {
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      }
    );
  }

  makeResizableElement(
    elementSelector: string,
    minHeight: number,
    minWidth: number
  ) {
    const element: HTMLElement | null = document.querySelector(elementSelector);
    const resizers = document.querySelectorAll(elementSelector + " .resizer");
    let original_width = 0;
    let original_height = 0;
    let original_x = 0;
    let original_y = 0;
    let original_mouse_x = 0;
    let original_mouse_y = 0;

    if (element) {
      for (let i = 0; i < resizers.length; i++) {
        const currentResizer = resizers[i];
        const resizeStart = (event: any) => {
          event.preventDefault();
          original_width = parseFloat(
            getComputedStyle(element, null)
              .getPropertyValue("width")
              .replace("px", "")
          );
          original_height = parseFloat(
            getComputedStyle(element, null)
              .getPropertyValue("height")
              .replace("px", "")
          );
          original_x = element.offsetLeft;
          original_y = element.offsetTop;
          const pageX =
            event.type === "touchstart"
              ? event.changedTouches[0].pageX
              : event.pageX;
          const pageY =
            event.type === "touchstart"
              ? event.changedTouches[0].pageY
              : event.pageY;
          original_mouse_x = pageX;
          original_mouse_y = pageY;
          window.addEventListener("mousemove", resize);
          window.addEventListener("mouseup", stopResize);
          window.addEventListener("touchmove", resize);
          window.addEventListener("touchend", stopResize);
        };

        currentResizer.addEventListener("mousedown", resizeStart);
        currentResizer.addEventListener("touchstart", resizeStart);

        const resize = (event: any) => {
          const pageX =
            event.type === "touchmove"
              ? event.changedTouches[0].pageX
              : event.pageX;
          const pageY =
            event.type === "touchmove"
              ? event.changedTouches[0].pageY
              : event.pageY;

          if (element) {
            if (currentResizer.classList.contains("bottom-right")) {
              const width = original_width + (pageX - original_mouse_x);
              const height = original_height + (pageY - original_mouse_y);
              if (width > minWidth) {
                element.style.width = width + "px";
              }
              if (height > minHeight) {
                element.style.height = height + "px";
              }
            } else if (currentResizer.classList.contains("bottom-left")) {
              const height = original_height + (pageY - original_mouse_y);
              const width = original_width - (pageX - original_mouse_x);
              if (height > minHeight) {
                element.style.height = height + "px";
              }
              if (width > minWidth) {
                element.style.width = width + "px";
                element.style.left =
                  original_x + (pageX - original_mouse_x) + "px";
              }
            } else if (currentResizer.classList.contains("top-right")) {
              const width = original_width + (pageX - original_mouse_x);
              const height = original_height - (pageY - original_mouse_y);
              if (width > minWidth) {
                element.style.width = width + "px";
              }
              if (height > minHeight) {
                element.style.height = height + "px";
                element.style.top =
                  original_y + (pageY - original_mouse_y) + "px";
              }
            } else {
              const width = original_width - (pageX - original_mouse_x);
              const height = original_height - (pageY - original_mouse_y);
              if (width > minWidth) {
                element.style.width = width + "px";
                element.style.left =
                  original_x + (pageX - original_mouse_x) + "px";
              }
              if (height > minHeight) {
                element.style.height = height + "px";
                element.style.top =
                  original_y + (pageY - original_mouse_y) + "px";
              }
            }
            this._syncCustomTagHostToResizable(element);
          }
        };

        const stopResize = () => {
          window.removeEventListener("mousemove", resize);
          window.removeEventListener("mouseup", stopResize);
          window.removeEventListener("touchmove", resize);
          window.removeEventListener("touchend", stopResize);
          if (element) {
            this._syncCustomTagHostToResizable(element);
          }
        };
      }
    }
  }

  private _syncCustomTagHostToResizable(resizable: HTMLElement): void {
    const host = resizable.closest('.sign-tag-element-container') as HTMLElement | null;
    if (!host) return;

    const width = Math.round(resizable.offsetWidth);
    const height = Math.round(resizable.offsetHeight);
    if (width <= 0 || height <= 0) return;

    host.style.width = `${width}px`;
    host.style.height = `${height}px`;
    host.style.removeProperty('max-width');
    host.style.removeProperty('max-height');
    host.setAttribute('data-kzn-user-sized', 'true');
  }

  // private async getElementImage(element: Element): Promise<string> {
  //   const commentOnlyInput = element.getElementsByClassName(
  //     Constants.SIGN_COMMENT_CLASS
  //   )[0] as HTMLElement;
  //   const imgStr = commentOnlyInput
  //     ? await htmlToImage.toPng(commentOnlyInput)
  //     : await element.getElementsByTagName("img")[0]?.src;
  //   return imgStr?.split("base64,")[1];
  // }

  /** Same base64 extraction used by Sign/Upload submit (Sign/Upload parity). */
  async extractSignImageBase64(container: Element): Promise<string> {
    return this.getElementImage(container);
  }

  /** Same frame as Sign/Upload and mail-link viewer (% of textLayer over canvas). */
  getPageTagCoordinateFrame(pageElement: HTMLElement): {
    refRect: DOMRect;
    refWidth: number;
    refHeight: number;
  } {
    return this._getPageTagCoordinateFrame(pageElement);
  }

  getCanonicalCustomTagSizePercents(
    tagType: string,
    pageWidth: number,
    pageHeight: number,
  ): { width: number; height: number } | null {
    return this._getCanonicalCustomTagSizePercents(tagType, pageWidth, pageHeight);
  }

 
  async rasterizeSelfSignInlineField(
    hostElement: HTMLElement,
    fieldType: string,
    widthPercent?: number,
    heightPercent?: number,
    pageWidth?: number,
    pageHeight?: number,
  ): Promise<string | null> {
    const content = hostElement.querySelector('.annotation-content') as HTMLElement | null;
    if (!content || !this._hasSelfSignInlineFieldValue(fieldType, content)) return null;

    const captureW =
      pageWidth && widthPercent
        ? Math.max(Math.round((widthPercent / 100) * pageWidth), 24)
        : Math.max(content.clientWidth, hostElement.clientWidth - 12, 24);
    const captureH =
      pageHeight && heightPercent
        ? Math.max(Math.round((heightPercent / 100) * pageHeight), 20)
        : Math.max(content.clientHeight, hostElement.clientHeight - 12, 20);

    const displayText = this._getSelfSignInlineFieldUploadValue(fieldType, content);
    const fieldColor = hostElement.dataset['fieldColor'] || DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
    const signImgStr = this._rasterizeSelfSignFieldCanvas(
      fieldType,
      content,
      displayText,
      captureW,
      captureH,
      fieldColor,
    );
    return signImgStr?.trim() ? signImgStr : null;
  }


  private _rasterizeSelfSignFieldCanvas(
    fieldType: string,
    content: HTMLElement,
    displayText: string,
    width: number,
    height: number,
    fieldColor: string = DEFAULT_SELF_SIGN_SIGNATURE_COLOR,
  ): string {
    const lower = (fieldType ?? '').toLowerCase();
    const isLabeledField = lower === 'text' || lower === 'initials' || lower === 'date';
    const isCommentField = lower === 'comment';
    const labelOverflow =
      isLabeledField || isCommentField ? SignService.SELF_SIGN_FIELD_LABEL_OVERFLOW_PX : 0;
    const innerH = isLabeledField
      ? Constants.CANONICAL_TAG_FIELD_HEIGHT_PX - labelOverflow
      : isCommentField
        ? Math.max(Math.round(height), Constants.CANONICAL_TAG_FIELD_HEIGHT_PX)
        : Math.max(Math.round(height), 20);
    const h = innerH + labelOverflow;

    let w: number;
    if (isLabeledField) {
      const value =
        displayText ||
        this._readSelfSignTagFieldValue(content, lower) ||
        '';
      w = this._resolveLabeledTagCaptureWidth(lower, value);
    } else {
      w = Math.max(Math.round(width), 24);
    }

    if (isCommentField) {
      const label =
        this._readSelfSignTagFieldLabel(content) ||
        Constants.DEFAULT_COMMENT_PREFIX.replace('{ROLE}', 'Signer');
      const body =
        displayText ||
        this._readSelfSignTagFieldValue(content, 'comment') ||
        content.querySelector('.self-sign-comment-field__body')?.textContent ||
        '';
      w = Math.max(
        w,
        this.getSelfSignCommentHostWidth(label, body.trim(), Constants.DEFAULT_COMMENT_SUFFIX),
      );
    }

    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.scale(scale, scale);

    if (lower === 'checkbox') {
      this._drawSelfSignCheckboxCanvas(ctx, content, w, innerH, fieldColor);
    } else if (lower === 'comment') {
      this._drawSelfSignCommentCanvas(ctx, content, displayText, w, innerH, fieldColor);
    } else {
      this._drawSelfSignLabeledFieldCanvas(
        ctx,
        content,
        this._resolveSelfSignFieldLabel(lower),
        displayText,
        w,
        innerH,
        fieldColor,
        labelOverflow,
      );
    }

    const dataUrl = canvas.toDataURL('image/png');
    const idx = dataUrl.indexOf('base64,');
    return idx !== -1 ? dataUrl.substring(idx + 7) : dataUrl;
  }

  private _resolveSelfSignFieldLabel(fieldType: string): string {
    switch (fieldType) {
      case 'text':
        return 'Text';
      case 'initials':
        return 'Initials';
      case 'date':
        return 'Date';
      default:
        return fieldType;
    }
  }

  /** Self-sign inline fields: transparent PNG so underlying PDF text stays visible. */
  private static readonly SELF_SIGN_CAPTURE_BACKGROUND = 'rgba(0,0,0,0)';

  private _clearSelfSignCanvas(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    ctx.clearRect(0, 0, w, h);
  }

  private _drawSelfSignLabeledFieldCanvas(
    ctx: CanvasRenderingContext2D,
    content: HTMLElement,
    label: string,
    value: string,
    w: number,
    boxH: number,
    fieldColor: string,
    labelOverflow = SignService.SELF_SIGN_FIELD_LABEL_OVERFLOW_PX,
  ): void {
    const boxTop = labelOverflow;
    const totalH = boxTop + boxH;

    this._clearSelfSignCanvas(ctx, w, totalH);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.38)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, boxTop + 0.5, w - 1, boxH - 1);

    ctx.font = '11px Arial, Helvetica, sans-serif';
    ctx.fillStyle = '#6b7280';
    const labelWidth = ctx.measureText(label).width;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.fillRect(10, boxTop - 7, labelWidth + 8, 14);
    ctx.fillStyle = '#6b7280';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, 14, boxTop + 0.5);

    let display =
      value ||
      this._readSelfSignTagFieldValue(content, label.toLowerCase()) ||
      '';
    if ((label || '').toLowerCase() === 'date') {
      display = this._formatDateTagDisplayValue(display);
    }
    ctx.fillStyle = fieldColor || '#111827';
    ctx.font = 'bold 14px Arial, Helvetica, sans-serif';
    ctx.textBaseline = 'middle';
    const valueY = boxTop + boxH / 2;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.strokeText(display, 10, valueY, w - 20);
    ctx.fillText(display, 10, valueY, w - 20);
  }

  private _drawSelfSignCommentCanvas(
    ctx: CanvasRenderingContext2D,
    content: HTMLElement,
    displayText: string,
    w: number,
    h: number,
    fieldColor: string,
  ): void {
    const label =
      this._readSelfSignTagFieldLabel(content) ||
      Constants.DEFAULT_COMMENT_PREFIX.replace('{ROLE}', 'Signer');
    const body =
      displayText ||
      this._readSelfSignCommentBody(content) ||
      '';
    const suffix = Constants.DEFAULT_COMMENT_SUFFIX;
    const labelOverflow = SignService.SELF_SIGN_FIELD_LABEL_OVERFLOW_PX;
    const innerH = h;
    const totalH = innerH + labelOverflow;
    const boxTop = labelOverflow;
    const padX = 10;

    this._clearSelfSignCanvas(ctx, w, totalH);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.38)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, boxTop + 0.5, w - 1, innerH - 1);

    ctx.font = '11px Arial, Helvetica, sans-serif';
    ctx.fillStyle = '#6b7280';
    const labelWidth = ctx.measureText(label).width;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.fillRect(padX, boxTop - 7, labelWidth + 8, 14);
    ctx.fillStyle = '#6b7280';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, padX + 4, boxTop + 0.5);

    ctx.fillStyle = fieldColor || '#111827';
    ctx.font = 'bold 14px Arial, Helvetica, sans-serif';
    ctx.textBaseline = 'middle';
    const bodyY = boxTop + innerH / 2;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.strokeText(body, padX, bodyY, w - padX * 2);
    ctx.fillText(body, padX, bodyY, w - padX * 2);

    const suffixMaxWidth = w - padX * 2;
    let suffixFontSize = 8;
    while (suffixFontSize > 6) {
      ctx.font = `${suffixFontSize}px Arial, Helvetica, sans-serif`;
      if (ctx.measureText(suffix).width <= suffixMaxWidth) break;
      suffixFontSize -= 0.5;
    }
    ctx.fillStyle = '#1565c0';
    ctx.textBaseline = 'bottom';
    ctx.fillText(suffix, padX, boxTop + innerH - 4, suffixMaxWidth);
  }

  private _drawSelfSignCheckboxCanvas(
    ctx: CanvasRenderingContext2D,
    content: HTMLElement,
    w: number,
    h: number,
    fieldColor: string,
  ): void {
    const input = content.querySelector(
      '.self-sign-tag-field__checkbox-input, input[type="checkbox"]',
    ) as HTMLInputElement | null;
    const labelEl = content.querySelector(
      '.sign-tag-checkbox-label, .self-sign-tag-field__checkbox-label',
    ) as HTMLInputElement | HTMLElement | null ?? content.querySelector('span');
    const label = labelEl instanceof HTMLInputElement
      ? (labelEl.value ?? '').trim()
      : (labelEl?.textContent ?? '').trim();
    const checked = input?.checked ?? false;
    const boxSize = 16;
    const boxX = 8;
    const boxY = (h - boxSize) / 2;

    this._clearSelfSignCanvas(ctx, w, h);

    if (checked) {
      ctx.fillStyle = '#3f51b5';
      ctx.fillRect(boxX, boxY, boxSize, boxSize);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(boxX + 3, boxY + boxSize / 2);
      ctx.lineTo(boxX + boxSize / 2 - 1, boxY + boxSize - 4);
      ctx.lineTo(boxX + boxSize - 3, boxY + 4);
      ctx.stroke();
    } else {
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.54)';
      ctx.lineWidth = 2;
      ctx.strokeRect(boxX + 1, boxY + 1, boxSize - 2, boxSize - 2);
    }

    if (label) {
      ctx.fillStyle = fieldColor;
      ctx.font = '13px Arial, Helvetica, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, boxX + boxSize + 8, h / 2, w - boxX - boxSize - 16);
    }
  }

  private async _rasterizeSelfSignFieldContent(
    content: HTMLElement,
    fieldType: string,
    width: number,
    height: number,
  ): Promise<string> {
    const displayText = this._getSelfSignInlineFieldUploadValue(fieldType, content);
    return this._rasterizeSelfSignFieldCanvas(fieldType, content, displayText, width, height);
  }

 
  private static readonly MINIMAL_PNG_BASE64 =
    'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';

  /** Extra top space so Material-style floating labels are not clipped in PDF output. */
  private static readonly SELF_SIGN_FIELD_LABEL_OVERFLOW_PX = 8;

  private _measureSelfSignInlineTextWidth(text: string, fontSize = 14): number {
    if (!text.trim()) return 0;
    if (typeof document === 'undefined') return text.length * (fontSize * 0.55);
    const measure = document.createElement('span');
    measure.style.cssText = [
      'position:absolute',
      'visibility:hidden',
      'white-space:nowrap',
      'pointer-events:none',
      `font-size:${fontSize}px`,
      'font-family:Arial, Helvetica, sans-serif',
      'font-weight:normal',
    ].join(';');
    measure.textContent = text;
    document.body.appendChild(measure);
    const width = measure.offsetWidth;
    measure.remove();
    return width;
  }

  /** Self-sign checkbox host width from label text (checkbox icon + gap + padding). */
  getSelfSignCheckboxHostWidth(label: string, fontSize = 13): number {
    const text = (label ?? '').trim() || 'Check to confirm';
    const textWidth = this._measureSelfSignInlineTextWidth(text, fontSize);
    return Math.max(
      Constants.CANONICAL_TAG_CHECKBOX_FIELD_WIDTH_PX,
      Math.ceil(textWidth + 56),
    );
  }

  /** Self-sign comment host width from label, body, and suffix text. */
  getSelfSignCommentHostWidth(label: string, body: string, suffix?: string): number {
    const suffixText = (suffix ?? Constants.DEFAULT_COMMENT_SUFFIX).trim();
    const labelText = (label ?? '').trim() || Constants.DEFAULT_COMMENT_PREFIX.replace('{ROLE}', 'Signer');
    const bodyText = (body ?? '').trim();

    const labelW = this._measureSelfSignInlineTextWidth(labelText, 11) + 16;
    const bodyW = bodyText ? this._measureSelfSignInlineTextWidth(bodyText, 14) : 0;
    const suffixW = this._measureSelfSignInlineTextWidth(suffixText, 8);

    return Math.max(
      Constants.MINIMUM_SIGN_COMMENT_WIDTH,
      Math.ceil(Math.max(labelW, bodyW, suffixW) + 24),
    );
  }

  /** Expand text/initials/checkbox host so long values are not clipped on capture or in the PDF. */
  prepareSelfSignTagHostForUpload(hostElement: HTMLElement, fieldType: string): void {
    const lower = (fieldType ?? '').toLowerCase();
    if (lower === 'checkbox') {
      let label = this._getSelfSignCheckboxLabel(hostElement);
      if (!label) {
        const labelInput = hostElement.querySelector(
          '.sign-tag-checkbox-label, .self-sign-tag-field__checkbox-label',
        ) as HTMLInputElement | null;
        label = (labelInput?.getAttribute('placeholder') ?? '').trim();
      }
      if (!label) return;

      const neededWidth = this.getSelfSignCheckboxHostWidth(label);

      hostElement.style.width = `${neededWidth}px`;
      hostElement.style.maxWidth = `${neededWidth}px`;

      const resizable = hostElement.querySelector('.resizable') as HTMLElement | null;
      if (resizable) {
        resizable.style.width = `${neededWidth}px`;
      }
      return;
    }

    if (lower !== 'text' && lower !== 'initials') return;

    const dataType = lower === 'initials' ? 'INITIALS' : 'TEXT';
    const input = hostElement.querySelector(
      `mat-form-field[data-type="${dataType}"] input`,
    ) as HTMLInputElement | null;
    const text = (input?.value ?? input?.getAttribute('data-value') ?? '').trim();
    if (!text) return;

    let neededWidth = this._resolveLabeledTagCaptureWidth(lower, text);

    const pageElement = hostElement.closest('.pdf-page') as HTMLElement | null;
    if (pageElement) {
      const frame = this._getPageTagCoordinateFrame(pageElement);
      const minWidth = Constants.CANONICAL_TAG_LABELED_FIELD_MIN_WIDTH_PX +
        Constants.CANONICAL_TAG_LABELED_FIELD_WIDTH_EXTRA_PX;
      neededWidth = Math.min(neededWidth, Math.max(frame.refWidth - 8, minWidth));
    }

    hostElement.style.width = `${neededWidth}px`;
    hostElement.style.maxWidth = `${neededWidth}px`;

    const resizable = hostElement.querySelector('.resizable') as HTMLElement | null;
    if (resizable) {
      resizable.style.width = `${neededWidth}px`;
    }
  }

  private _adjustSelfSignTagBoundsForLabelOverflow(
    bounds: {
      xPercent: number;
      yPercent: number;
      imgWidthPercent: number;
      imgHeightPercent: number;
    },
    pageElement: HTMLElement,
    innerHeightPx: number,
  ): {
    xPercent: number;
    yPercent: number;
    imgWidthPercent: number;
    imgHeightPercent: number;
  } {
    const frame = this._getPageTagCoordinateFrame(pageElement);
    const overflow = SignService.SELF_SIGN_FIELD_LABEL_OVERFLOW_PX;
    const totalH = innerHeightPx + overflow;
    const overflowYPercent = (overflow / frame.refHeight) * 100;
    const totalHeightPercent = (totalH / frame.refHeight) * 100;

    return {
      xPercent: bounds.xPercent,
      yPercent: this._formatTagPercent(Math.max(0, bounds.yPercent - overflowYPercent)),
      imgWidthPercent: bounds.imgWidthPercent,
      imgHeightPercent: this._formatTagPercent(totalHeightPercent),
    };
  }

  private _isSelfSignLabeledUploadType(uploadType: string): boolean {
    return uploadType === 'TEXT' || uploadType === 'INITIALS' || uploadType === 'DATE' || uploadType === 'COMMENT';
  }

  private _resolveCompactLabeledTagInnerHeight(uploadType: string): number {
    const normalized = this.normalizeUploadSignatureType(uploadType);
    if (normalized === 'TEXT' || normalized === 'INITIALS' || normalized === 'DATE') {
      return Constants.CANONICAL_TAG_FIELD_HEIGHT_PX - SignService.SELF_SIGN_FIELD_LABEL_OVERFLOW_PX;
    }
    return Constants.CANONICAL_TAG_FIELD_HEIGHT_PX;
  }

  private _resolveLabeledTagCaptureWidth(fieldType: string, displayText: string): number {
    const lower = (fieldType ?? '').toLowerCase();
    const label = this._resolveSelfSignFieldLabel(lower);
    const padX = 20;
    const labelW = this._measureSelfSignInlineTextWidth(label, 11);
    let w = Math.max(
      Constants.CANONICAL_TAG_LABELED_FIELD_MIN_WIDTH_PX,
      Math.ceil(labelW + padX),
    );
    const value = (displayText ?? '').trim();
    if (value) {
      const textW = this._measureSelfSignInlineTextWidth(value, 14);
      w = Math.max(w, Math.ceil(textW + padX));
    }
    return w + Constants.CANONICAL_TAG_LABELED_FIELD_WIDTH_EXTRA_PX;
  }

  private _applyLabeledTagUploadSizePercents(
    bounds: {
      xPercent: number;
      yPercent: number;
      imgWidthPercent: number;
      imgHeightPercent: number;
    },
    pageElement: HTMLElement,
    uploadType: string,
    displayText: string,
    innerH: number,
  ): {
    xPercent: number;
    yPercent: number;
    imgWidthPercent: number;
    imgHeightPercent: number;
  } {
    const normalized = this.normalizeUploadSignatureType(uploadType);
    if (normalized !== 'TEXT' && normalized !== 'INITIALS' && normalized !== 'DATE') {
      return bounds;
    }
    const frame = this._getPageTagCoordinateFrame(pageElement);
    const captureW = this._resolveLabeledTagCaptureWidth(normalized.toLowerCase(), displayText);
    const updated = {
      ...bounds,
      imgWidthPercent: this._formatTagPercent((captureW / frame.refWidth) * 100),
      imgHeightPercent: this._formatTagPercent(
        (Constants.CANONICAL_TAG_FIELD_HEIGHT_PX / frame.refHeight) * 100,
      ),
    };
    return this._adjustSelfSignTagBoundsForLabelOverflow(updated, pageElement, innerH);
  }

  private _resolveSelfSignTagCaptureInnerHeight(
    uploadType: string,
    hostElement: HTMLElement,
  ): number {
    const normalized = this.normalizeUploadSignatureType(uploadType);
    if (normalized === 'TEXT' || normalized === 'INITIALS' || normalized === 'DATE') {
      return this._resolveCompactLabeledTagInnerHeight(uploadType);
    }
    return Math.max(hostElement.offsetHeight, Constants.CANONICAL_TAG_FIELD_HEIGHT_PX);
  }

  private _resolveSelfSignCaptureSize(
    hostElement: HTMLElement,
    pageElement: HTMLElement,
    bounds: { imgWidthPercent: number; imgHeightPercent: number },
  ): { width: number; height: number } {
    const frame = this._getPageTagCoordinateFrame(pageElement);
    const width = Math.max(
      hostElement.offsetWidth,
      Math.round((bounds.imgWidthPercent / 100) * frame.refWidth),
      24,
    );
    const height = Math.max(
      hostElement.offsetHeight,
      Math.round((bounds.imgHeightPercent / 100) * frame.refHeight),
      20,
    );
    return { width, height };
  }

 
  getHostUploadPercentBounds(
    hostElement: HTMLElement,
    pageElement: HTMLElement,
    uploadType: string,
  ): {
    xPercent: number;
    yPercent: number;
    imgWidthPercent: number;
    imgHeightPercent: number;
  } {
    const frame = this._getPageTagCoordinateFrame(pageElement);
    const normalized = this.normalizeUploadSignatureType(uploadType);

    let xPercent: number;
    let yPercent: number;
    let imgWidthPercent: number;
    let imgHeightPercent: number;

    if (hostElement.classList.contains('annotation-wrapper')) {
      if (normalized === 'SIGNATURE') {
        return this._getSelfSignSignaturePercentBounds(hostElement, pageElement);
      }

      const tx = parseFloat(hostElement.getAttribute('data-x') || '0') || 0;
      const ty = parseFloat(hostElement.getAttribute('data-y') || '0') || 0;
      const leftPx = (parseFloat(hostElement.style.left) || 0) + tx;
      const topPx = (parseFloat(hostElement.style.top) || 0) + ty;
      xPercent = this._formatTagPercent((leftPx / frame.refWidth) * 100);
      yPercent = this._formatTagPercent((topPx / frame.refHeight) * 100);
      imgWidthPercent = this._formatTagPercent(
        (hostElement.offsetWidth / frame.refWidth) * 100,
      );
      imgHeightPercent = this._formatTagPercent(
        (hostElement.offsetHeight / frame.refHeight) * 100,
      );
    } else {
      const positionEl = this._resolveSignTagPositionRect(hostElement);
      const rect = positionEl.getBoundingClientRect();
      xPercent = this._formatTagPercent(
        ((rect.left - frame.refRect.left) / frame.refWidth) * 100,
      );
      yPercent = this._formatTagPercent(
        ((rect.top - frame.refRect.top) / frame.refHeight) * 100,
      );
      imgWidthPercent = this._formatTagPercent((rect.width / frame.refWidth) * 100);
      imgHeightPercent = this._formatTagPercent((rect.height / frame.refHeight) * 100);
    }

    if (
      normalized === 'SIGNATURE' &&
      (this._isSignHereHostedDrag(hostElement) || this._isSelfSignAnnotationHost(hostElement))
    ) {
      const canonical = this._getCanonicalSignHereSizePercents(frame.refWidth, frame.refHeight);
      imgWidthPercent = this._formatTagPercent(canonical.width);
      imgHeightPercent = this._formatTagPercent(canonical.height);
    } else if (
      normalized === 'COMMENT' &&
      !hostElement.classList.contains('annotation-wrapper--self-sign-comment-filled')
    ) {
      imgWidthPercent = this._formatTagPercent(Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE);
      imgHeightPercent = this._formatTagPercent(Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE);
    } else if (
      !hostElement.classList.contains('self-sign-tag-host') &&
      !hostElement.classList.contains('annotation-wrapper')
    ) {
      const canonical = this._getCanonicalCustomTagSizePercents(
        normalized,
        frame.refWidth,
        frame.refHeight,
      );
      if (canonical) {
        imgWidthPercent = this._formatTagPercent(canonical.width);
        imgHeightPercent = this._formatTagPercent(canonical.height);
      }
    }

    return { xPercent, yPercent, imgWidthPercent, imgHeightPercent };
  }

  /**
   * Self-sign signature coords — same layout px as field placement (style.left),
   * with canonical 19.5% stamp size (matches Sign/Upload).
   */
  private _getSelfSignSignaturePercentBounds(
    hostElement: HTMLElement,
    pageElement: HTMLElement,
  ): {
    xPercent: number;
    yPercent: number;
    imgWidthPercent: number;
    imgHeightPercent: number;
  } {
    const frame = this._getPageTagCoordinateFrame(pageElement);
    const tx = parseFloat(hostElement.getAttribute('data-x') || '0') || 0;
    const ty = parseFloat(hostElement.getAttribute('data-y') || '0') || 0;
    const leftPx = (parseFloat(hostElement.style.left) || 0) + tx;
    const topPx = (parseFloat(hostElement.style.top) || 0) + ty;
    const canonical = this._getCanonicalSignHereSizePercents(frame.refWidth, frame.refHeight);

    return {
      xPercent: this._formatTagPercent((leftPx / frame.refWidth) * 100),
      yPercent: this._formatTagPercent((topPx / frame.refHeight) * 100),
      imgWidthPercent: this._formatTagPercent(canonical.width),
      imgHeightPercent: this._formatTagPercent(canonical.height),
    };
  }

  /** Unified self-sign upload entry — mirrors Sign/Upload `getRequestDetails`. */
  async collectSelfSignUploadEntry(
    hostElement: HTMLElement,
    pageElement: HTMLElement,
    pageIndex: number,
    fieldType: string,
  ): Promise<(ISignUploadSignature & { type: string }) | null> {
    const uploadType = this.normalizeUploadSignatureType(
      fieldType.toLowerCase() === 'esign' ? 'SIGNATURE' : fieldType,
    );

    if (uploadType === 'COMMENT') {
      const content = hostElement.querySelector('.annotation-content') as HTMLElement | null;
      const isSelfSignFilled = hostElement.classList.contains(
        'annotation-wrapper--self-sign-comment-filled',
      );
      const hasCommentImage = !!content?.querySelector('img[data-value-available="true"]');
      if (!hasCommentImage || isSelfSignFilled) {
        return this.collectSelfSignInlineFieldUploadEntry(
          hostElement,
          pageElement,
          pageIndex,
          fieldType,
        );
      }
    }

    if (['TEXT', 'DATE', 'CHECKBOX', 'INITIALS'].includes(uploadType)) {
      return this.collectSelfSignInlineFieldUploadEntry(
        hostElement,
        pageElement,
        pageIndex,
        fieldType,
      );
    }

    const bounds = this.getHostUploadPercentBounds(hostElement, pageElement, uploadType);
    const details = await this.getRequestDetails(hostElement, pageElement);
    if (!details && uploadType !== 'SIGNATURE') return null;

    let signImgStr = (details?.signImgStr ?? '').trim();
    if (!signImgStr) {
      signImgStr = await this._extractSelfSignSignatureBase64(hostElement);
    }
    if (!signImgStr && uploadType === 'SIGNATURE') return null;

    if (signImgStr && ['SIGNATURE', 'STAMP'].includes(uploadType)) {
      try {
        signImgStr = await stripSignatureBackgroundFromBase64(signImgStr);
      } catch {
        /* keep original image */
      }
    }

    const host = hostElement as HTMLElement;

    return {
      pageIndex,
      xPercent: bounds.xPercent,
      yPercent: bounds.yPercent,
      signImgStr: signImgStr || SignService.MINIMAL_PNG_BASE64,
      imgWidthPercent: bounds.imgWidthPercent,
      imgHeightPercent: bounds.imgHeightPercent,
      signComment: details?.signComment ?? '',
      signatureId: details?.signatureId ?? '',
      isComment: uploadType === 'COMMENT',
      commentText: details?.commentText || host.dataset['commentText'] || '',
      type: uploadType,
    };
  }

  /** Collect one Sign/Upload signature entry from a self-sign field host. */
  async collectUploadSignatureFromHost(
    hostElement: HTMLElement,
    pageElement: HTMLElement,
    pageIndex: number,
  ): Promise<(ISignUploadSignature & { type: string }) | null> {
    return this.collectSelfSignUploadEntry(hostElement, pageElement, pageIndex, 'esign');
  }

  /** Collect text/date/checkbox — commentText for display, signImgStr never empty. */
  async collectSelfSignInlineFieldUploadEntry(
    hostElement: HTMLElement,
    pageElement: HTMLElement,
    pageIndex: number,
    fieldType: string,
  ): Promise<(ISignUploadSignature & { type: string }) | null> {
    const uploadType = this.normalizeUploadSignatureType(fieldType);
    if (!['TEXT', 'DATE', 'CHECKBOX', 'INITIALS', 'COMMENT'].includes(uploadType)) return null;

    const isSignTagHost = hostElement.classList.contains('self-sign-tag-host');
    const content = hostElement.querySelector('.annotation-content') as HTMLElement | null;

    if (isSignTagHost) {
      if (!this._hasSelfSignSignTagValue(fieldType, hostElement)) return null;

      this.prepareSelfSignTagHostForUpload(hostElement, fieldType);

      const commentText = this._getSelfSignSignTagUploadValue(fieldType, hostElement);
      if (!commentText && uploadType !== 'CHECKBOX') return null;

      let bounds = this.getHostUploadPercentBounds(hostElement, pageElement, uploadType);
      const innerH = this._resolveSelfSignTagCaptureInnerHeight(uploadType, hostElement);
      const captureW =
        uploadType === 'TEXT' || uploadType === 'INITIALS' || uploadType === 'DATE'
          ? this._resolveLabeledTagCaptureWidth(fieldType, commentText)
          : this._resolveSelfSignCaptureSize(hostElement, pageElement, bounds).width;
      const captureNode = hostElement.querySelector(`.${Constants.SIGN_COMMENT_CLASS}`) as HTMLElement | null;
      const fieldColor = hostElement.dataset['fieldColor'] || DEFAULT_SELF_SIGN_SIGNATURE_COLOR;

      bounds = this._applyLabeledTagUploadSizePercents(
        bounds,
        pageElement,
        uploadType,
        commentText,
        innerH,
      );
      if (uploadType === 'COMMENT') {
        bounds = this._adjustSelfSignTagBoundsForLabelOverflow(bounds, pageElement, innerH);
      }

      const signImgStr =
        this._rasterizeSelfSignFieldCanvas(
          fieldType,
          captureNode ?? hostElement,
          commentText,
          captureW,
          innerH,
          fieldColor,
        ) ||
        (captureNode
          ? await this._rasterizeHTMLElementToBase64(
              captureNode,
              captureW,
              innerH + SignService.SELF_SIGN_FIELD_LABEL_OVERFLOW_PX,
            )
          : '') ||
        (await this.getElementImage(hostElement));
      if (!signImgStr.trim()) return null;

      return {
        pageIndex,
        xPercent: bounds.xPercent,
        yPercent: bounds.yPercent,
        signImgStr,
        imgWidthPercent: bounds.imgWidthPercent,
        imgHeightPercent: bounds.imgHeightPercent,
        signComment: '',
        signatureId: '',
        isComment: uploadType === 'COMMENT',
        commentText,
        type: uploadType,
      };
    }

    const captureContent =
      content ??
      (hostElement.querySelector('.annotation-content') as HTMLElement | null);
    if (!captureContent || !this._hasSelfSignInlineFieldValue(fieldType, captureContent)) {
      const hostCommentText = (hostElement.dataset['commentText'] ?? '').toString().trim();
      if (uploadType !== 'COMMENT' || !hostCommentText) {
        return null;
      }
    }

    let commentText =
      (captureContent
        ? this._getSelfSignInlineFieldUploadValue(fieldType, captureContent)
        : '') ||
      (hostElement.dataset['commentText'] ?? '').toString().trim();
    if (!commentText && uploadType !== 'CHECKBOX') return null;

    let bounds = this.getHostUploadPercentBounds(hostElement, pageElement, uploadType);
    const innerH = this._resolveSelfSignTagCaptureInnerHeight(uploadType, hostElement);
    const captureW =
      uploadType === 'TEXT' || uploadType === 'INITIALS' || uploadType === 'DATE'
        ? this._resolveLabeledTagCaptureWidth(fieldType, commentText)
        : this._resolveSelfSignCaptureSize(hostElement, pageElement, bounds).width;

    bounds = this._applyLabeledTagUploadSizePercents(
      bounds,
      pageElement,
      uploadType,
      commentText,
      innerH,
    );
    if (uploadType === 'COMMENT') {
      bounds = this._adjustSelfSignTagBoundsForLabelOverflow(bounds, pageElement, innerH);
    }

    const rasterContent =
      captureContent ??
      (hostElement.querySelector('.annotation-content') as HTMLElement);

    const signImgStr =
      this._rasterizeSelfSignFieldCanvas(
        fieldType,
        rasterContent,
        commentText,
        captureW,
        innerH,
        hostElement.dataset['fieldColor'] || DEFAULT_SELF_SIGN_SIGNATURE_COLOR,
      ) ||
      (await this._captureSelfSignTagFieldDom(
        rasterContent,
        captureW,
        innerH + SignService.SELF_SIGN_FIELD_LABEL_OVERFLOW_PX,
        hostElement,
      )) ||
      '';
    if (!signImgStr.trim()) return null;

    return {
      pageIndex,
      xPercent: bounds.xPercent,
      yPercent: bounds.yPercent,
      signImgStr,
      imgWidthPercent: bounds.imgWidthPercent,
      imgHeightPercent: bounds.imgHeightPercent,
      signComment: '',
      signatureId: '',
      isComment: uploadType === 'COMMENT',
      commentText,
      type: uploadType,
    };
  }

  /** Tag `Text` value — same as Sign/Upload `commentText` / `data-value`. */
  getSelfSignInlineTagText(hostElement: HTMLElement, fieldType: string): string {
    const content = hostElement.querySelector('.annotation-content') as HTMLElement | null;
    if (!content) return '';
    return this._getSelfSignInlineFieldUploadValue(fieldType, content);
  }

  private async _extractSelfSignSignatureBase64(hostElement: HTMLElement): Promise<string> {
    const img = hostElement.querySelector(
      'img[data-value-available="true"], img[data-type="SIGNATURE"], img[signatureid], .annotation-content img',
    ) as HTMLImageElement | null;
    if (!img?.src?.trim()) return '';
    return this.getElementImage(hostElement);
  }

  private _hasSelfSignInlineFieldValue(fieldType: string, content: HTMLElement): boolean {
    if ((fieldType ?? '').toLowerCase() === 'comment') {
      return !!this._readSelfSignCommentBody(content);
    }
    return !!this._getSelfSignInlineFieldUploadValue(fieldType, content) ||
      (fieldType ?? '').toLowerCase() === 'checkbox';
  }

  private async _captureSelfSignTagFieldDom(
    content: HTMLElement,
    width: number,
    height: number,
    hostElement?: HTMLElement,
  ): Promise<string | null> {
    const labeledNode = content.querySelector('.self-sign-tag-field') as HTMLElement | null;
    if (!labeledNode && !hostElement) return null;

    const captureRoot = hostElement ?? labeledNode;
    if (!captureRoot) return null;

    await new Promise((resolve) => setTimeout(resolve, 32));

    try {
      const captureW = Math.max(width, captureRoot.offsetWidth, 24);
      const captureH = Math.max(height, captureRoot.offsetHeight, 20);
      const dataUrl = await htmlToImage.toPng(captureRoot, {
        width: captureW,
        height: captureH,
        pixelRatio: 2,
        backgroundColor: SignService.SELF_SIGN_CAPTURE_BACKGROUND,
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          return !node.classList.contains('close-btn')
            && !node.classList.contains('annotation-move-icon');
        },
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
        },
      });
      const idx = dataUrl.indexOf('base64,');
      return idx !== -1 ? dataUrl.substring(idx + 7) : dataUrl;
    } catch {
      return null;
    }
  }

  private _readSelfSignTagFieldLabel(content: HTMLElement): string {
    return (
      content.querySelector('.self-sign-comment-field__label')?.textContent ??
      content.querySelector('.self-sign-tag-field__label')?.textContent ??
      ''
    ).trim();
  }

  private static readonly DEFAULT_SELF_SIGN_CHECKBOX_LABELS = new Set([
    'Check to confirm',
    'Checkbox',
    'Editable Label',
  ]);

  /** Only user-edited checkbox labels are sent to the API — not the default placeholder copy. */
  private _resolveSelfSignCheckboxUploadLabel(label: string): string {
    const text = (label ?? '').trim();
    if (!text || SignService.DEFAULT_SELF_SIGN_CHECKBOX_LABELS.has(text)) {
      return '';
    }
    return text;
  }

  private _getSelfSignCheckboxLabel(hostElement: HTMLElement): string {
    const labelInput = hostElement.querySelector(
      '.sign-tag-checkbox-label, .self-sign-tag-field__checkbox-label',
    ) as HTMLInputElement | null;
    if (labelInput) {
      return (labelInput.value ?? labelInput.getAttribute('data-value') ?? '').trim();
    }

    const labelEl = hostElement.querySelector('.self-sign-tag-field__checkbox-label') as HTMLElement | null
      ?? hostElement.querySelector('.sign-tag-checkbox-row span')
      ?? hostElement.querySelector('mat-checkbox[data-type="CHECKBOX"]');
    return (labelEl?.textContent ?? '').trim();
  }

  private _readSelfSignCommentBody(content: HTMLElement): string {
    const selfSignBody = content.querySelector(
      '.self-sign-comment-field__body',
    ) as HTMLElement | null;
    if (selfSignBody) {
      return (selfSignBody.textContent ?? '').trim();
    }

    const hiddenImg = content.querySelector(
      'img[data-value-available="true"]',
    ) as HTMLImageElement | null;
    const fromImg = (hiddenImg?.getAttribute('data-value') ?? '').trim();
    if (fromImg) return fromImg;

    const legacyBody = content.querySelector(
      '.self-sign-tag-field__comment-body',
    ) as HTMLElement | null;
    if (legacyBody) {
      return (legacyBody.textContent ?? '').trim();
    }

    if (content.querySelector('img[data-value-available="true"]')) {
      return '';
    }

    return (content.innerText ?? content.textContent ?? '').trim();
  }

  private _formatDateTagDisplayValue(raw: string): string {
    const s = (raw ?? '').trim();
    if (!s) return '';

    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;

    const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!slash) return s;

    const first = parseInt(slash[1], 10);
    const second = parseInt(slash[2], 10);
    const yyyy = slash[3];
    if (!first || !second || first > 31 || second > 31) return s;

    if (first > 12) {
      return `${String(first).padStart(2, '0')}/${String(second).padStart(2, '0')}/${yyyy}`;
    }
    if (second > 12) {
      return `${String(second).padStart(2, '0')}/${String(first).padStart(2, '0')}/${yyyy}`;
    }

    return `${String(first).padStart(2, '0')}/${String(second).padStart(2, '0')}/${yyyy}`;
  }

  private _readDateTagDisplayValue(host: ParentNode | null | undefined): string {
    if (!host) return '';
    const input = host.querySelector(
      'mat-form-field[data-type="DATE"] input, .self-sign-tag-field__date-input, input[type="date"]',
    ) as HTMLInputElement | null;
    if (!input) return '';

    const fromDataValue = (input.getAttribute('data-value') ?? '').trim();
    if (fromDataValue) return this._formatDateTagDisplayValue(fromDataValue);

    return this._formatDateTagDisplayValue((input.value ?? '').trim());
  }

  private _readSelfSignTagFieldValue(content: HTMLElement, fieldType: string): string {
    const lower = (fieldType ?? '').toLowerCase();
    const valueEl = content.querySelector('.self-sign-tag-field__value') as HTMLElement | null;
    if (valueEl) {
      return (valueEl.textContent ?? '').trim();
    }

    if (lower === 'date') {
      return this._readDateTagDisplayValue(content);
    }

    const matFieldType = lower.toUpperCase();
    const matInput = content.querySelector(
      `mat-form-field[data-type="${matFieldType}"] input`,
    ) as HTMLInputElement | null;
    if (matInput) {
      return (matInput.value ?? matInput.getAttribute('data-value') ?? '').trim();
    }

    if (lower === 'comment') {
      return this._readSelfSignCommentBody(content);
    }

    return (content.innerText ?? content.textContent ?? '').trim();
  }

  /** Same value shape as Sign/Upload `commentText` / `data-value` on sign tags. */
  private _getSelfSignInlineFieldUploadValue(fieldType: string, content: HTMLElement): string {
    const lower = (fieldType ?? '').toLowerCase();
    const tagValue = this._readSelfSignTagFieldValue(content, fieldType);

    if (lower === 'text') {
      const text = tagValue || (content.innerText ?? content.textContent ?? '').trim();
      return text.length > 0 && text !== 'Editable Text' && text !== 'Enter text here' ? text : '';
    }
    if (lower === 'initials') {
      const text = tagValue || (content.innerText ?? content.textContent ?? '').trim();
      return text.length > 0 && text !== 'Initial' && text !== 'Initials' ? text : '';
    }
    if (lower === 'comment') {
      const text = this._readSelfSignCommentBody(content) || tagValue;
      return text.length > 0 && text !== 'Comment' && text !== 'Click to add comment' ? text : '';
    }
    if (lower === 'date') {
      return tagValue;
    }
    if (lower === 'checkbox') {
      const input = content.querySelector(
        '.self-sign-tag-field__checkbox-input, input[type="checkbox"]',
      ) as HTMLInputElement | null;
      const label = this._resolveSelfSignCheckboxUploadLabel(
        this._getSelfSignCheckboxLabel(content),
      );
      if (input) {
        input.setAttribute('data-value', input.checked ? 'true' : '');
        input.setAttribute('data-value-available', input.checked ? 'true' : 'false');
      }
      return label;
    }
    return '';
  }

  private async _rasterizeHTMLElementToBase64(
    node: HTMLElement,
    width?: number,
    height?: number,
  ): Promise<string> {
    await new Promise((resolve) => setTimeout(resolve, 50));
    const w = Math.max(width ?? node.scrollWidth, node.clientWidth, 1);
    const h = Math.max(height ?? node.scrollHeight, node.clientHeight, 1);
    const dataUrl = await htmlToImage.toPng(node, {
      width: w,
      height: h,
      pixelRatio: 2,
      backgroundColor: SignService.SELF_SIGN_CAPTURE_BACKGROUND,
      style: {
        transform: 'scale(1)',
        transformOrigin: 'top left',
      },
    });
    const base64Index = dataUrl.indexOf('base64,');
    return base64Index !== -1 ? dataUrl.substring(base64Index + 7) : dataUrl;
  }

  /** Sign/Upload uses `SIGNATURE`; tag API uses `Esign`. */
  normalizeUploadSignatureType(type: string): string {
    const raw = (type ?? '').trim();
    if (!raw) return '';
    const lower = raw.toLowerCase();
    if (lower === 'esign' || lower === 'signature') return 'SIGNATURE';
    if (lower === 'initials') return 'INITIALS';
    if (lower === 'stamp') return 'STAMP';
    if (lower === 'comment') return 'COMMENT';
    if (lower === 'text') return 'TEXT';
    if (lower === 'date') return 'DATE';
    if (lower === 'checkbox') return 'CHECKBOX';
    return raw.toUpperCase();
  }

  private _isSelfSignAnnotationHost(el: Element): boolean {
    const host = el as HTMLElement;
    return (
      host.classList?.contains('annotation-wrapper') ||
      host.classList?.contains('self-sign-tag-host') ||
      host.dataset?.['selfSignSigned'] === 'true' ||
      host.classList?.contains('annotation-wrapper--signed') ||
      !!host.querySelector?.('[data-value-available="true"][data-type="SIGNATURE"]')
    );
  }

  private _hasSelfSignSignTagValue(fieldType: string, hostElement: HTMLElement): boolean {
    return !!this._getSelfSignSignTagUploadValue(fieldType, hostElement) ||
      (fieldType ?? '').toLowerCase() === 'checkbox';
  }

  private _getSelfSignSignTagUploadValue(fieldType: string, hostElement: HTMLElement): string {
    const lower = (fieldType ?? '').toLowerCase();

    if (lower === 'text') {
      const input = hostElement.querySelector(
        'mat-form-field[data-type="TEXT"] input',
      ) as HTMLInputElement | null;
      const text = (input?.value ?? input?.getAttribute('data-value') ?? '').trim();
      return text && text !== 'Editable Text' && text !== 'Enter text here' ? text : '';
    }

    if (lower === 'initials') {
      const input = hostElement.querySelector(
        'mat-form-field[data-type="INITIALS"] input',
      ) as HTMLInputElement | null;
      const text = (input?.value ?? input?.getAttribute('data-value') ?? '').trim();
      return text && text !== 'Initial' && text !== 'Initials' ? text : '';
    }

    if (lower === 'date') {
      return this._readDateTagDisplayValue(hostElement);
    }

    if (lower === 'checkbox') {
      const checkbox = hostElement.querySelector('mat-checkbox[data-type="CHECKBOX"], .sign-tag-checkbox-row[data-type="CHECKBOX"] mat-checkbox');
      const input = checkbox?.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
      const label = this._resolveSelfSignCheckboxUploadLabel(
        this._getSelfSignCheckboxLabel(hostElement),
      );
      if (input) {
        input.setAttribute('data-value', input.checked ? 'true' : '');
        input.setAttribute('data-value-available', input.checked ? 'true' : 'false');
      }
      return label;
    }

    return '';
  }

  private async getElementImage(element: Element): Promise<string> {
  const commentOnlyInput = element.getElementsByClassName(
    Constants.SIGN_COMMENT_CLASS
  )[0] as HTMLElement | undefined;

  let imgStr: string | null = null;

  if (commentOnlyInput) {
    const node = commentOnlyInput;
    const host = element as HTMLElement;

    await new Promise(res => setTimeout(res, 100));

    const captureW = Math.max(node.scrollWidth, node.clientWidth, host.offsetWidth || 0, 24);
    const captureH = Math.max(node.scrollHeight, node.clientHeight, host.offsetHeight || 0, 20);

    imgStr = await htmlToImage.toPng(node, {
      width: captureW,
      height: captureH,
      pixelRatio: 2,
      backgroundColor: SignService.SELF_SIGN_CAPTURE_BACKGROUND,
      style: {
        transform: 'scale(1)',
        transformOrigin: 'top left',
      },
    });
  } else {
    const img = element.getElementsByTagName("img")[0] as HTMLImageElement | undefined;
    if (img?.src) {
      const isStamp =
        !!img.closest('[data-type="STAMP"]') ||
        img.classList.contains('custom-tag__stamp-image') ||
        img.getAttribute('data-type') === 'STAMP';
      imgStr = isStamp
        ? await this._rasterizeStampImageAtNaturalSize(img)
        : img.src;
    }
  }


  if (!imgStr) return "";

  const base64Index = imgStr.indexOf("base64,");
  if (base64Index !== -1) {
    return imgStr.substring(base64Index + 7);
  }

  if (/^https?:\/\//i.test(imgStr) || imgStr.startsWith('/')) {
    try {
      return await this._rasterizeImageToBase64(
        imgStr.startsWith('/') ? `${window.location.origin}${imgStr}` : imgStr,
      );
    } catch {
      return "";
    }
  }

  return imgStr;
}

  private _rasterizeImageToBase64(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(img.naturalWidth || img.width || 1, 1);
          canvas.height = Math.max(img.naturalHeight || img.height || 1, 1);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas unavailable'));
            return;
          }
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          const idx = dataUrl.indexOf('base64,');
          resolve(idx !== -1 ? dataUrl.substring(idx + 7) : dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load signature image'));
      img.src = src;
    });
  }

  private _rasterizeStampImageAtNaturalSize(img: HTMLImageElement): Promise<string> {
    return new Promise((resolve, reject) => {
      const draw = () => {
        const width = Math.max(img.naturalWidth || img.width || 1, 1);
        const height = Math.max(img.naturalHeight || img.height || 1, 1);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(this._extractBase64FromDataUrl(img.src));
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/png');
        resolve(this._extractBase64FromDataUrl(dataUrl));
      };

      if (img.complete && (img.naturalWidth || img.width)) {
        draw();
        return;
      }

      img.onload = () => draw();
      img.onerror = () => reject(new Error('Failed to load stamp image'));
    });
  }

  private _extractBase64FromDataUrl(value: string): string {
    const base64Index = value.indexOf('base64,');
    return base64Index !== -1 ? value.substring(base64Index + 7) : value;
  }

  async getRequestDetails(signImagesOnPage: any, pageElement: any): Promise<any> {
    const frame = this._getPageTagCoordinateFrame(pageElement);
    const positionRect = this._resolveSignTagPositionRect(signImagesOnPage as HTMLElement);
    const signImageRect = positionRect.getBoundingClientRect();
    const xPercent = this._formatTagPercent(
      ((signImageRect.left - frame.refRect.left) / frame.refWidth) * 100
    );
    const yPercent = this._formatTagPercent(
      ((signImageRect.top - frame.refRect.top) / frame.refHeight) * 100
    );
    const tagHost = signImagesOnPage as HTMLElement;
    const commentOnlyInput = signImagesOnPage.getElementsByClassName(
      Constants.SIGN_COMMENT_CLASS
    )[0] as HTMLElement | undefined;
    const typedField = tagHost.querySelector('[data-type]') as HTMLElement | null;
    const imgEle = signImagesOnPage.getElementsByTagName('img')[0] as HTMLElement | undefined;
    const signatureId = imgEle?.getAttribute('signatureid') ?? '';
    const isValueAvailable = !!tagHost.querySelector('[data-value-available="true"]');
    const type = typedField?.getAttribute('data-type') || '';
    const uploadType = this.normalizeUploadSignatureType(type);
    let commentText = (commentOnlyInput ?? signImagesOnPage).querySelectorAll('[data-value]')?.[0]?.getAttribute('data-value') || '';
    if (uploadType === 'DATE') {
      commentText = this._readDateTagDisplayValue(tagHost) || this._formatDateTagDisplayValue(commentText);
    }
    if (!isValueAvailable && uploadType !== 'SIGNATURE' && uploadType !== 'COMMENT') return;

    let imgWidthPercent = this._formatTagPercent(
      (signImageRect.width / frame.refWidth) * 100
    );
    let imgHeightPercent = this._formatTagPercent(
      (signImageRect.height / frame.refHeight) * 100
    );
    if (
      uploadType === 'SIGNATURE' &&
      (this._isSignHereHostedDrag(signImagesOnPage) ||
        this._isSelfSignAnnotationHost(signImagesOnPage))
    ) {
      const canonical = this._getCanonicalSignHereSizePercents(frame.refWidth, frame.refHeight);
      imgWidthPercent = this._formatTagPercent(canonical.width);
      imgHeightPercent = this._formatTagPercent(canonical.height);
    } else if (uploadType === 'COMMENT' || commentOnlyInput) {
      imgWidthPercent = this._formatTagPercent(Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE);
      imgHeightPercent = this._formatTagPercent(Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE);
    } else if (this._isCustomTagDrag(signImagesOnPage) && uploadType) {
      const canonical = this._getCanonicalCustomTagSizePercents(uploadType, frame.refWidth, frame.refHeight);
      if (canonical) {
        imgWidthPercent = this._formatTagPercent(canonical.width);
        imgHeightPercent = this._formatTagPercent(canonical.height);
      }
    }

    const signComment =
      signImagesOnPage.getElementsByClassName("sign-comment")[0]
        ?.innerHTML || "";

    const suffixIcon = signImagesOnPage.getElementsByClassName("mat-mdc-form-field-icon-suffix");
    if (suffixIcon.length > 0) suffixIcon[0].style.display = "none";

    let signImgStr = '';
    const canvasCaptureTypes = new Set(['TEXT', 'INITIALS', 'DATE', 'CHECKBOX', 'COMMENT']);
    const hasRasterizedValueImage = !!tagHost.querySelector(
      'img[data-value-available="true"], img[data-type="COMMENT"]',
    );
    const shouldUseCanvasCapture =
      canvasCaptureTypes.has(uploadType) &&
      !(uploadType === 'COMMENT' && hasRasterizedValueImage && !commentText.trim());

    if (shouldUseCanvasCapture) {
      const captureNode =
        (commentOnlyInput ??
          tagHost.querySelector(`.${Constants.SIGN_COMMENT_CLASS}`) ??
          tagHost) as HTMLElement;
      const resolvedText =
        commentText ||
        this._getSelfSignSignTagUploadValue(uploadType.toLowerCase(), tagHost) ||
        this._readSelfSignTagFieldValue(captureNode, uploadType.toLowerCase());
      const isCompactLabeled =
        uploadType === 'TEXT' || uploadType === 'INITIALS' || uploadType === 'DATE';
      const captureW = isCompactLabeled
        ? this._resolveLabeledTagCaptureWidth(uploadType.toLowerCase(), resolvedText)
        : Math.max(
            signImageRect.width,
            captureNode.offsetWidth,
            captureNode.scrollWidth,
            24,
          );
      const innerH =
        isCompactLabeled
          ? this._resolveCompactLabeledTagInnerHeight(uploadType)
          : Math.max(
              signImageRect.height,
              captureNode.offsetHeight,
              Constants.CANONICAL_TAG_FIELD_HEIGHT_PX,
            );
      signImgStr =
        this._rasterizeSelfSignFieldCanvas(
          uploadType.toLowerCase(),
          captureNode,
          resolvedText,
          captureW,
          innerH,
          DEFAULT_SELF_SIGN_SIGNATURE_COLOR,
        ) ?? '';
      if (isCompactLabeled && signImgStr) {
        imgWidthPercent = this._formatTagPercent((captureW / frame.refWidth) * 100);
        imgHeightPercent = this._formatTagPercent(
          (Constants.CANONICAL_TAG_FIELD_HEIGHT_PX / frame.refHeight) * 100,
        );
      }
    }

    if (!signImgStr.trim()) {
      signImgStr = await this.getElementImage(signImagesOnPage);
    }

    if (suffixIcon.length > 0) suffixIcon[0].style.display = "block";

    const resolvedUploadType = uploadType || (this._isSignHereHostedDrag(signImagesOnPage) ? 'SIGNATURE' : '');
    if (this.normalizeUploadSignatureType(resolvedUploadType) === 'SIGNATURE') {
      if (!signImgStr?.trim()) return null;
      const signatureHost = (signImagesOnPage as HTMLElement).closest(
        '.box-sign-here, [data-kzn-tag-id]',
      ) as HTMLElement | null;
      if (signatureHost?.querySelector('img.img-sign-here')) return null;
      const hasSignatureDom =
        this.hostHasPlacedSignatureContent(signImagesOnPage as HTMLElement) ||
        this.hostHasPlacedSignatureContent(signatureHost);
      if (!hasSignatureDom) return null;
    }

    return {
      xPercent,
      yPercent,
      signImgStr,
      imgWidthPercent,
      imgHeightPercent,
      signComment,
      signatureId,
      type: uploadType || type,
      commentText
    }
  }

  getRequiredTagPageIndexes(legacyTagIds?: Iterable<string>): number[] {
    const pages = new Set<number>();
    for (const tagId of this.collectAllRequiredTagIds(legacyTagIds)) {
      pages.add(this._resolveRequiredTagPageNumber(tagId) - 1);
    }
    return Array.from(pages.values()).sort((a, b) => a - b);
  }

  getRequiredFieldsCount(legacyTagIds?: Iterable<string>): number {
    const stableIds = this.collectAllRequiredTagIds(legacyTagIds);
    if (stableIds.length) return stableIds.length;

    return (
      this.getCompletedFieldsCount(legacyTagIds) +
      this.countUnsignedSignHerePlaceholders() +
      this.countUnsignedCustomTagFields()
    );
  }

  syncRequiredFieldsCount(legacyTagIds?: Iterable<string>): void {
    this.allTagsCount = this.getRequiredFieldsCount(legacyTagIds);
  }

  isRequiredSignTagFilled(tagIndex: number): boolean {
    const tag = this.signTags[tagIndex];
    if (!tag || tag.Type === EnumSignTagTypes.COMMENT) return true;

    const tagId = `pt-${tagIndex}`;
    const host = document.querySelector(`[data-kzn-tag-id="${tagId}"]`) as HTMLElement | null;

    if (SignService.isSignatureTagType(tag.Type)) {
      if (host?.querySelector('img.img-sign-here')) return false;
      const record = this.getPlacedSignature(tagId);
      if (this.hasPlacedSignatureRecordContent(record)) return true;
      return host ? this.hostHasPlacedSignatureContent(host) : false;
    }

    if (host) {
      return (
        !!host.querySelector('[data-value-available="true"]') ||
        !!host.querySelector('.cdk-drag [data-value-available="true"]')
      );
    }

    const record = this.getPlacedSignature(tagId);
    return this.hasPlacedSignatureRecordContent(record);
  }

  countRequiredSignatureTags(legacyTagIds?: Iterable<string>): number {
    let count = 0;
    for (const tagId of this.collectAllRequiredTagIds(legacyTagIds)) {
      if (this.isSignatureRequiredTagId(tagId)) count++;
    }
    return count;
  }

  countUploadedSignatureFields(signatures: Array<ISignUploadSignature>): number {
    return signatures.filter((signature) => {
      if (signature.isComment) return false;
      if (!signature.signImgStr?.trim()) return false;
      const uploadType = this.normalizeUploadSignatureType(
        (signature as ISignUploadSignature & { type?: string }).type || 'SIGNATURE',
      );
      return uploadType === 'SIGNATURE';
    }).length;
  }

  private _collectMissingSignaturePageNumbers(
    legacyTagIds?: Iterable<string>,
    signatures?: Array<ISignUploadSignature>,
  ): number[] {
    const requiredIds = this.collectAllRequiredTagIds(legacyTagIds);
    const missingPages = new Set<number>();

    const isSignatureTagFilled = (tagId: string): boolean => {
      if (this.isRequiredTagIdFilled(tagId)) return true;
      return this.hasPlacedSignatureRecordContent(this.getPlacedSignature(tagId));
    };

    const trackSignatureTag = (tagId: string): void => {
      if (!this.isSignatureRequiredTagId(tagId)) return;
      if (isSignatureTagFilled(tagId)) return;
      const pageNumber = this._resolveRequiredTagPageNumber(tagId);
      if (pageNumber > 0) missingPages.add(pageNumber);
    };

    if (requiredIds.length) {
      for (const tagId of requiredIds) {
        trackSignatureTag(tagId);
      }
    } else if (legacyTagIds) {
      for (const tagId of legacyTagIds) {
        if (!tagId || tagId.startsWith('dp-') || tagId.startsWith('kc-')) continue;
        trackSignatureTag(tagId);
      }
    }

    document
      .querySelectorAll(
        '.box-sign-here.sign:not(.sign-tag-comment-container):not(.comment-placeholder-container) img.img-sign-here',
      )
      .forEach((img) => {
        const host = img.closest('.box-sign-here') as HTMLElement | null;
        if (!host) return;
        const tagId = host.getAttribute('data-kzn-tag-id') || '';
        if (tagId && this.hasPlacedSignature(tagId)) return;
        if (this.isSignHereHostFilled(host, tagId)) return;
        const hostPage = host.getAttribute('page');
        const pageNumber = hostPage != null && hostPage !== '' ? +hostPage + 1 : 0;
        if (pageNumber > 0) missingPages.add(pageNumber);
      });

    return Array.from(missingPages.values()).sort((a, b) => a - b);
  }

  private _collectMissingRequiredFieldPageNumbers(legacyTagIds?: Iterable<string>): number[] {
    const requiredIds = this.collectAllRequiredTagIds(legacyTagIds);
    const pageStats = new Map<number, { required: number; missing: number }>();

    for (const tagId of requiredIds) {
      if (this.isSignatureRequiredTagId(tagId)) continue;
      const pageNumber = this._resolveRequiredTagPageNumber(tagId);
      if (pageNumber <= 0) continue;
      const stat = pageStats.get(pageNumber) ?? { required: 0, missing: 0 };
      stat.required++;
      if (!this.isRequiredTagIdFilled(tagId)) stat.missing++;
      pageStats.set(pageNumber, stat);
    }

    return Array.from(pageStats.entries())
      .filter(([, stat]) => stat.missing > 0)
      .map(([pageNumber]) => pageNumber)
      .sort((a, b) => a - b);
  }

  private _formatMissingSignatureMessage(pages: number[]): string {
    const uniquePages = Array.from(new Set(pages.filter((page) => page > 0))).sort((a, b) => a - b);
    if (!uniquePages.length) {
      return 'Required signature missing. Please add required signature and submit.';
    }
    if (uniquePages.length === 1) {
      return `Required signature missing on page number ${uniquePages[0]}. Please add required signature and submit.`;
    }
    return `Required signatures missing on page number ${uniquePages.join(' & ')}. Please add required signatures and submit.`;
  }

  private _formatMissingRequiredFieldMessage(pages: number[]): string {
    const uniquePages = Array.from(new Set(pages.filter((page) => page > 0))).sort((a, b) => a - b);
    if (!uniquePages.length) {
      return 'Required field missing, please add and then submit.';
    }
    if (uniquePages.length === 1) {
      return `Required field missing at page number ${uniquePages[0]}, please add and then submit.`;
    }
    return `Required fields missing at page numbers ${uniquePages.join(', ')}, please add and then submit.`;
  }

  formatMissingSignaturePagesMessage(pages: number[]): string {
    return this._formatMissingSignatureMessage(pages);
  }

  buildRequiredSignatureMissingMessage(
    legacyTagIds?: Iterable<string>,
    signatures?: Array<ISignUploadSignature>,
  ): string {
    return this._formatMissingSignatureMessage(
      this._collectMissingSignaturePageNumbers(legacyTagIds, signatures),
    );
  }

  validateRequiredSignTagsFilled(
    legacyTagIds?: Iterable<string>,
    signatures?: Array<ISignUploadSignature>,
  ): { valid: boolean; errorMessage?: string } {
    const requiredIds = this.collectAllRequiredTagIds(legacyTagIds);
    if (!requiredIds.length) {
      const unsignedPlaceholders = this.countUnsignedSignHerePlaceholders();
      const unsignedCustom = this.countUnsignedCustomTagFields();
      if (unsignedPlaceholders > 0 || unsignedCustom > 0) {
        return {
          valid: false,
          errorMessage: this.buildRequiredSignatureMissingMessage(legacyTagIds, signatures),
        };
      }
      return { valid: true };
    }

    const missingSignaturePages = this._collectMissingSignaturePageNumbers(legacyTagIds, signatures);
    if (missingSignaturePages.length) {
      return {
        valid: false,
        errorMessage: this._formatMissingSignatureMessage(missingSignaturePages),
      };
    }

    const missingFieldPages = this._collectMissingRequiredFieldPageNumbers(legacyTagIds);
    if (missingFieldPages.length) {
      return {
        valid: false,
        errorMessage: this._formatMissingRequiredFieldMessage(missingFieldPages),
      };
    }

    return { valid: true };
  }

  getCompletedFieldsCount(legacyTagIds?: Iterable<string>): number {
    const requiredIds = this.collectAllRequiredTagIds(legacyTagIds);
    if (requiredIds.length) {
      let count = 0;
      for (const tagId of requiredIds) {
        if (this.isRequiredTagIdFilled(tagId)) count++;
      }
      return count;
    }

    if (legacyTagIds) {
      let count = 0;
      for (const tagId of legacyTagIds) {
        if (!tagId || tagId.startsWith('dp-') || tagId.startsWith('kc-')) continue;
        if (this.isRequiredTagIdFilled(tagId)) count++;
      }
      return count;
    }

    let count = 0;
    try {
      document.querySelectorAll('.box-sign-here.sign[data-kzn-tag-id]').forEach((node) => {
        const host = node as HTMLElement;
        const tagId = host.getAttribute('data-kzn-tag-id') || '';
        if (this.isSignHereHostFilled(host, tagId)) count++;
      });
      document.querySelectorAll('.sign-tag-element-container[data-kzn-tag-id]').forEach((node) => {
        const host = node as HTMLElement;
        const tagId = host.getAttribute('data-kzn-tag-id') || '';
        if (this.isRequiredTagIdFilled(tagId)) count++;
      });
    } catch {
      return 0;
    }
    return count;
  }

  async getPDFSignatures(): Promise<Array<ISignUploadSignature>> {
    const request: Array<ISignUploadSignature> = [];
    const collectedTagIds = new Set<string>();
    const pages = document.getElementsByClassName("page");

    for (let i = 0; i < pages.length; i++) {
      const signImagesOnPage = pages[i].getElementsByClassName("cdk-drag");
      for (let j = 0; j < signImagesOnPage?.length; j++) {
        const cdkEl = signImagesOnPage[j] as HTMLElement;
        const requestObj = await this.getRequestDetails(cdkEl, pages[i]);
        if (requestObj) {
          request.push({ pageIndex: i, ...requestObj });
          const tagId =
            cdkEl.closest('[data-kzn-tag-id]')?.getAttribute('data-kzn-tag-id') ?? '';
          if (tagId) collectedTagIds.add(tagId);
        }
      }
    }

    for (const [tagId, rec] of this._placedSignatures.entries()) {
      if (collectedTagIds.has(tagId) || !rec.submitSnapshot?.signImgStr) continue;
      request.push({
        pageIndex: rec.pageIndex,
        ...rec.submitSnapshot,
      });
      collectedTagIds.add(tagId);
    }

    const seenSignatureIds = new Set<string>();
    for (const sig of request) {
      if (sig.signatureId) {
        if (seenSignatureIds.has(sig.signatureId)) {
          sig.signatureId = '';
        } else {
          seenSignatureIds.add(sig.signatureId);
        }
      }
    }

    this.syncRequiredFieldsCount();

    return request;
  }

  addSignImage(
    imageBase64: string,
    left: string,
    top: string,
    pageIndex: number = 0,
    viewContainerRef: ViewContainerRef,
    isDraggable: boolean = true,
    isCommentOnly: boolean = false,
    signComment?: string,
    signHereContainer?: HTMLElement,
    comment?: string,
    signatureId?: string,
    isResponsiveContainer?: boolean,
    disableDelete?: boolean
  ) {
    const pages = document.getElementsByClassName("page");

    /** Match predefined sign-here spans: percentage size is relative to .textLayer, not .page */
    let targetContainer = signHereContainer;
    let insetLeft = left;
    let insetTop = top;
    let responsive = !!isResponsiveContainer;
    let createdContainer: HTMLElement | null = null;

    if (!targetContainer && !isCommentOnly) {
      const pageEl = pages[pageIndex] as HTMLElement | undefined;
      const textLayerEl =
        pageEl?.querySelector(".textLayer") as HTMLElement | null;
      if (textLayerEl) {
        const parseLeadingNumber = (s: string): number => {
          const m = String(s || "").trim().match(/^(-?\d*\.?\d+)/);
          return m ? parseFloat(m[1]) : 0;
        };

        const w = Math.max(textLayerEl.clientWidth, 1);
        const h = Math.max(textLayerEl.clientHeight, 1);
        /* left/top are px offsets from the textLayer origin (not .page). */
        const lx = parseLeadingNumber(left);
        const ly = parseLeadingNumber(top);

        const span = document.createElement("span");
        span.className = "box-sign-here sign-here-standard";
        span.style.position = "absolute";
        span.style.left = `${Math.max(0, Math.min(100, (lx / w) * 100))}%`;
        span.style.top = `${Math.max(0, Math.min(100, (ly / h) * 100))}%`;
        span.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
        span.setAttribute('data-kzn-tag-id', `sh-${pageIndex}-${this.generateUUID()}`);
        this._applyMobileSignatureTagDimensions(span);
        if (typeof window === 'undefined' || window.innerWidth > 768) {
          span.style.minWidth = "120px";
        }

        textLayerEl.appendChild(span);
        this._ensureOverflowVisible(span);

        targetContainer = span;
        createdContainer = span;
        insetLeft = "0";
        insetTop = "0";
        responsive = true;
      }
    }

    const element: HTMLElement = this.getSignDragResizeElement(
      imageBase64,
      insetLeft,
      insetTop,
      viewContainerRef,
      isDraggable,
      isCommentOnly,
      signComment,
      comment,
      signatureId,
      responsive,
      disableDelete
    );

    if (targetContainer) {
      this.removeSignHerePlaceholderImages(targetContainer);
      targetContainer.classList.add('sign-here-filled');
      targetContainer.appendChild(element);
    } else if (pages[pageIndex]) {
      pages[pageIndex].appendChild(element);
    }

    if (createdContainer) {
      const newTagId = createdContainer.getAttribute('data-kzn-tag-id') || '';
      if (newTagId) {
        createdContainer.setAttribute('data-kzn-signhere-bound', 'true');
        this.registerPlacedSignature(newTagId, {
          imageBase64,
          isCommentOnly,
          signComment,
          comment,
          signatureId,
          isResponsiveContainer: responsive,
          isDraggable,
          disableDelete: !!disableDelete,
          pageIndex,
          hostGeometry: {
            left: createdContainer.style.left || '',
            top: createdContainer.style.top || '',
            width: createdContainer.style.width || '',
            height: createdContainer.style.height || '',
            className: createdContainer.className || 'box-sign-here sign-here-standard',
            page: pageIndex.toString(),
          },
        });
        void this.refreshPlacedSignatureSnapshot(newTagId, viewContainerRef);
      }
    }

    for (let i = 0; i < pages.length; ++i) {
      pages[i].removeEventListener("click", () => { });
      pages[i].removeEventListener("mousemove", () => { });
    }
  }

  getSignDragResizeElement(
    imageBase64: string,
    left: string,
    top: string,
    viewContainerRef: ViewContainerRef,
    isDraggable: boolean = true,
    isCommentOnly: boolean,
    signComment?: string,
    comment?: string,
    signatureId?: string,
    isResponsiveContainer?: boolean,
    disableDelete?: boolean
  ): HTMLElement {
    const componentRef = viewContainerRef.createComponent(
      SignDragResizeComponent
    );
    componentRef.instance.imageBase64 = imageBase64;
    componentRef.instance.signComment = signComment;
    componentRef.instance.left = left;
    componentRef.instance.top = top;
    componentRef.instance.isCommentOnly = isCommentOnly;
    componentRef.instance.comment = comment;
    componentRef.instance.isDraggable = isDraggable;
    componentRef.instance.signatureId = signatureId ?? '';
    componentRef.instance.isResponsiveContainer = isResponsiveContainer;
    componentRef.instance.disableDelete = disableDelete ?? false;
    componentRef.instance.destroyComponent = () => {
      let htmlElement = (componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
      const parent = htmlElement.parentElement as HTMLElement | null;
      const registryId = parent?.getAttribute?.('data-kzn-tag-id') ?? '';
      if (parent?.nodeName === "SPAN") {
        parent.remove();
      } else if (parent && parent.contains(htmlElement)) {
        parent.removeChild(htmlElement);
      }
      componentRef.destroy();
      if (registryId) this._placedSignatures.delete(registryId);
    };
    return componentRef.location.nativeElement;
  }

  addSign(
    imageBase64: string,
    viewContainerRef: ViewContainerRef,
    isCommentOnly: boolean = false,
    signComment?: string,
    comment?: string,
    signatureId?: string,
    isResponsiveContainer?: boolean
  ) {
    let img: HTMLElement | undefined = document.createElement("img");
    img.setAttribute("src", imageBase64);
    img.style.position = "absolute";
    if (!isCommentOnly) {
      img.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
      img.style.height = Constants.DEFAULT_SIGN_IMAGE_HEIGHT;
    }

    const textLayers = document.getElementsByClassName("textLayer");
    for (let i = 0; i < textLayers?.length; i++) {
      (textLayers[i] as HTMLElement).style.pointerEvents = "none";
    }

    const pages = document.getElementsByClassName("page");
    for (let i = 0; i < pages.length; i++) {
      const pageEl = pages[i] as HTMLElement;
      const textLayerEl = pageEl.querySelector('.textLayer') as HTMLElement | null;

      pages[i].addEventListener("mouseenter", () => {
        if (img && textLayerEl) {
          textLayerEl.appendChild(img);
        } else if (img) {
          pages[i].appendChild(img);
        }
      });

      pages[i].addEventListener("mousemove", (event: any) => {
        if (!img) return;
        const refEl = textLayerEl ?? pageEl;
        const refRect = refEl.getBoundingClientRect();
        img.style.left = event.clientX - refRect.left + 10 + "px";
        img.style.top = event.clientY - refRect.top + 10 + "px";
      });

      pages[i].addEventListener("click", (event: any) => {
        if (img) {
          const refEl = textLayerEl ?? pageEl;
          const refRect = refEl.getBoundingClientRect();
          const left = `${event.clientX - refRect.left + 10}px`;
          const top = `${event.clientY - refRect.top + 10}px`;
          this.addSignImage(
            imageBase64,
            left,
            top,
            i,
            viewContainerRef,
            true,
            isCommentOnly,
            signComment,
            undefined,
            comment,
            signatureId,
            isResponsiveContainer
          );
          if (img.parentElement) {
            img.parentElement.removeChild(img);
          }
          img = undefined;

          for (let i = 0; i < textLayers?.length; i++) {
            (textLayers[i] as HTMLElement).style.pointerEvents = "all";
          }
        }
      });
    }
  }

  renderSignHereElement(element: HTMLElement): HTMLElement {
    const tagId = element.getAttribute('data-kzn-tag-id') || '';
    if (this.isSignHereHostFilled(element, tagId)) {
      this.removeSignHerePlaceholderImages(element);
      element.classList.add('box-sign-here', 'sign', 'sign-here-filled');
      return element;
    }
    if (this.hostHasPlacedSignatureContent(element)) {
      this.removeSignHerePlaceholderImages(element);
      element.classList.add('box-sign-here', 'sign', 'sign-here-filled');
      return element;
    }
    const existingImg = element.querySelector('img.img-sign-here');
    if (
      existingImg &&
      element.classList.contains('box-sign-here') &&
      element.classList.contains('sign')
    ) {
      return element;
    }

    element.innerHTML = "";
    element.classList.add("box-sign-here");
    element.classList.add("sign");
    const img = document.createElement("img");
    const signHereSrc = "assets/sign-here.png";
    img.className = "img-sign-here";
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.display = "block";
   
    img.addEventListener(
      "error",
      () => {
        if (img.getAttribute("data-retry") === "1") return;
        img.setAttribute("data-retry", "1");
        img.setAttribute("src", `${signHereSrc}?r=${Date.now()}`);
      },
      { once: true },
    );
    img.setAttribute("src", signHereSrc);
    element.appendChild(img);
    this._prepareSignHereHost(element);

    return element;
  }

  renderCommentElement(element: HTMLElement, pageIndex: number, viewContainerRef: ViewContainerRef, disableDelete: boolean = false, className: string = ''): void {
    element.innerHTML = "";
    element.className = (`${className} box-sign-here`).trim();

    this.addSignImage(
      '',
      '0',
      '0',
      pageIndex,
      viewContainerRef,
      false,
      true,
      '',
      element,
      '',
      undefined,
      false,
      disableDelete
    );

    this._ensureOverflowVisible(element);
  }

  renderTagElement(element: HTMLElement, label: string, tag: EnumSignTagTypes, isCustomTagElementStatic = true, onRemoveElement?: () => void): HTMLElement {
    return this.renderSelfSignTagElement(
      element,
      label,
      tag,
      onRemoveElement,
      undefined,
      isCustomTagElementStatic,
    );
  }

  /** Self-sign editor fields — same sign-tag UI with drag, delete, and 4 corner resize handles. */
  renderSelfSignTagElement(
    element: HTMLElement,
    label: string,
    tag: EnumSignTagTypes,
    onRemoveElement?: () => void,
    options?: { initialTagValue?: string; initialDate?: Date },
    isCustomTagElementStatic = false,
  ): HTMLElement {
    const componentRef: ComponentRef<SignTagElementComponent> = createComponent(
      SignTagElementComponent,
      {
        environmentInjector: this._appRef.injector,
      },
    );
    componentRef.instance.label = label;
    componentRef.instance.tag = tag;
    componentRef.instance.isCustomTagElementStatic = isCustomTagElementStatic;
    componentRef.instance.destroyComponent = onRemoveElement;

    if (options?.initialTagValue) {
      componentRef.instance.tagValue = options.initialTagValue;
    }
    if (options?.initialDate && tag === EnumSignTagTypes.DATE) {
      componentRef.instance.dateObject = options.initialDate;
      componentRef.instance.onDateChange(options.initialDate);
    }

    this._appRef.attachView(componentRef.hostView);
    const domElement = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
    element.appendChild(domElement);
    (element as HTMLElement & { __selfSignTagComponentRef?: ComponentRef<SignTagElementComponent> })
      .__selfSignTagComponentRef = componentRef;

    this._ensureOverflowVisible(element);

    return element;
  }

  private _ensureOverflowVisible(el: HTMLElement): void {
    el.style.setProperty('overflow', 'visible', 'important');
    let parent = el.parentElement;
    while (parent) {
      if (
        parent.classList.contains('textLayer') ||
        parent.classList.contains('canvasWrapper') ||
        parent.classList.contains('page')
      ) {
        parent.style.setProperty('overflow', 'visible', 'important');
      }
      if (parent.classList.contains('pdfViewer')) {
        break;
      }
      parent = parent.parentElement;
    }
  }

  private _getSignHerePageMount(from: HTMLElement): HTMLElement | null {
    const page = from.classList.contains('page')
      ? from
      : (from.closest('.page') as HTMLElement | null);
    if (!page) return null;
    page.style.setProperty('overflow', 'visible', 'important');
    page.style.setProperty('position', 'relative', 'important');
    return page;
  }

  private _getTagMountLayer(pageEl: HTMLElement): HTMLElement {
    return (
      (pageEl.querySelector('.annotationLayer') as HTMLElement | null) ??
      (pageEl.querySelector('.textLayer') as HTMLElement | null) ??
      pageEl
    );
  }

  private _destroySelfSignTagComponentRef(host: HTMLElement): void {
    const keyed = host as HTMLElement & {
      __selfSignTagComponentRef?: ComponentRef<SignTagElementComponent>;
    };
    const ref = keyed.__selfSignTagComponentRef;
    if (!ref) return;
    try {
      this._appRef.detachView(ref.hostView);
      ref.destroy();
    } catch {
      /* ignore */
    }
    delete keyed.__selfSignTagComponentRef;
  }

  private _shouldSkipInteractiveCustomTagHost(
    pageTag: ISignTag,
    isSignerRole: boolean,
  ): boolean {
    if (this._isViewModeDocument()) return true;
    if (!isSignerRole && pageTag.Type !== EnumSignTagTypes.COMMENT) return true;
    return false;
  }

  private _isCustomTagHostFilled(host: HTMLElement): boolean {
    return (
      !!host.querySelector('[data-value-available="true"]') ||
      !!host.querySelector('img[data-value-available="true"]') ||
      !!host.querySelector('.cdk-drag [data-value-available="true"]')
    );
  }

  private _hideTagHostForReviewDisplay(host: HTMLElement): void {
    host.style.setProperty('display', 'none', 'important');
    host.style.setProperty('visibility', 'hidden', 'important');
    host.style.setProperty('pointer-events', 'none', 'important');
  }

  private _applyFilledCustomTagReadOnlyState(host: HTMLElement, tagType: EnumSignTagTypes | string): void {
    host.classList.add('sign-tag-element-container', 'sign-tag-field-completed');
    host.setAttribute('data-tag-type', String(tagType));
  }

  private _resolveSignTagHost(pageEl: HTMLElement, tagKey: string): HTMLElement | null {
    const onPage = pageEl.querySelector(`[data-kzn-tag-id="${tagKey}"]`) as HTMLElement | null;
    if (onPage?.isConnected) return onPage;
    const global = document.querySelector(`[data-kzn-tag-id="${tagKey}"]`) as HTMLElement | null;
    return global?.isConnected ? global : null;
  }

  private _ensureSignModeCustomTagHost(
    existing: HTMLElement,
    pageTag: ISignTag,
    pageIdx: number,
    viewContainerRef: ViewContainerRef,
    tagKey: string,
    isSignerRole: boolean,
  ): void {
    if (this._shouldSkipInteractiveCustomTagHost(pageTag, isSignerRole)) {
      this._hideTagHostForReviewDisplay(existing);
      return;
    }

    if (this._isCustomTagHostFilled(existing)) {
      if (!existing.querySelector('kzn-sign-tag-element')) {
        this._hideTagHostForReviewDisplay(existing);
        return;
      }
      this._applyFilledCustomTagReadOnlyState(existing, pageTag.Type);
      this._applyCustomTagHostDimensions(existing, pageTag.Type, pageTag);
      existing.setAttribute('page', String(pageIdx));
      return;
    }

    const hasUi =
      !!existing.querySelector('kzn-sign-tag-element') ||
      !!existing.querySelector('[data-type]') ||
      existing.childElementCount > 0;
    if (!hasUi) {
      this._destroySelfSignTagComponentRef(existing);
      existing.className = (existing.classList + ' sign-tag-element-container').trim();
      existing.setAttribute('data-tag-type', String(pageTag.Type));
      this.renderTagElement(
        existing,
        pageTag.Type === EnumSignTagTypes.CHECKBOX && !pageTag.Text ? 'Checkbox' : pageTag.Text,
        pageTag.Type,
      );
      this._applyCustomTagHostDimensions(existing, pageTag.Type, pageTag);
    } else {
      this._applyCustomTagHostDimensions(existing, pageTag.Type, pageTag);
    }
    existing.setAttribute('page', String(pageIdx));
    this._restoreSignatureIfRegistered(existing, pageIdx, viewContainerRef, tagKey);
  }

  private _mountSignHereOnPage(host: HTMLElement, pageEl: HTMLElement): void {
    const mount = this._getTagMountLayer(pageEl);
    if (host.parentElement !== mount) {
      mount.appendChild(host);
    }
    this._ensureOverflowVisible(host);
  }

  private _pageRelativePercentFromRect(
    pageEl: HTMLElement,
    targetRect: DOMRect,
  ): { left: string; top: string } {
    const pageRect = pageEl.getBoundingClientRect();
    const pw = Math.max(pageRect.width, 1);
    const ph = Math.max(pageRect.height, 1);
    return {
      left: `${Math.max(0, Math.min(92, ((targetRect.left - pageRect.left) / pw) * 100))}%`,
      top: `${Math.max(0, Math.min(92, ((targetRect.top - pageRect.top) / ph) * 100))}%`,
    };
  }

  private _syncExistingSignHereHost(
    host: HTMLElement,
    pageIdx: number,
    viewContainerRef: ViewContainerRef,
    signHereElements: HTMLElement[],
    isSignerRole: boolean = true,
  ): void {
    const existingTagId = host.getAttribute('data-kzn-tag-id') || '';
    const isCommentLike =
      host.classList.contains('sign-tag-comment-container') ||
      host.classList.contains('comment-placeholder-container');
    if (!isSignerRole && !isCommentLike) {
      this.removeSignHerePlaceholderImages(host);
      if (!this.isSignHereHostFilled(host, existingTagId)) {
        this._hideUnsignedSignHereHost(host);
      }
      return;
    }
    const hasPlacedSig =
      !!host.querySelector(
        '.cdk-drag img[data-type="SIGNATURE"], .cdk-drag img[signatureid]',
      ) ||
      (!!existingTagId && this.hasPlacedSignature(existingTagId));
    const hasPlaceholderImg = !!host.querySelector('img.img-sign-here');
    if (!isCommentLike) {
      if (this.isSignHereHostFilled(host, existingTagId)) {
        this.removeSignHerePlaceholderImages(host);
        host.classList.add('sign-here-filled');
      } else if (!hasPlacedSig && !hasPlaceholderImg) {
        if (existingTagId && this.hasPlacedSignature(existingTagId)) {
          this._restoreSignatureIfRegistered(host, pageIdx, viewContainerRef, existingTagId);
        } else {
          this.renderSignHereElement(host);
        }
      } else if (hasPlacedSig && hasPlaceholderImg) {
        this.removeSignHerePlaceholderImages(host);
        host.classList.add('sign-here-filled');
      }
    }
    const isUserDragPlaceholder =
      existingTagId.startsWith('dp-') ||
      host.getAttribute('data-drag-placeholder') === 'true';
    if (!existingTagId.startsWith('pt-') && !isUserDragPlaceholder) {
      signHereElements.push(host);
    }
    if (existingTagId) {
      this._restoreSignatureIfRegistered(host, pageIdx, viewContainerRef, existingTagId);
    }
    if (!isCommentLike) {
      this._prepareSignHereHost(host);
      this._applySignHereHostDimensions(host, pageIdx);
    }
  }

  private _prepareSignHereHost(host: HTMLElement): void {
    host.classList.add('box-sign-here', 'sign');
    host.style.setProperty('position', 'absolute', 'important');
    host.style.setProperty('opacity', '1', 'important');
    host.style.setProperty('visibility', 'visible', 'important');
    host.style.setProperty('overflow', 'visible', 'important');
    host.style.setProperty('transform', 'none', 'important');
    host.style.setProperty('transform-origin', 'initial', 'important');
    host.style.setProperty('pointer-events', 'auto', 'important');
    host.style.setProperty('z-index', '25', 'important');
    host.style.setProperty('display', 'block', 'important');
    host.style.setProperty('line-height', 'normal', 'important');
    host.style.setProperty('letter-spacing', 'normal', 'important');
    host.style.setProperty('white-space', 'normal', 'important');
    this._ensureOverflowVisible(host);
  }

  private _clearKeywordAnchorText(anchor: HTMLElement): void {
    anchor.textContent = '';
    anchor.style.setProperty('font-size', '0', 'important');
    anchor.style.setProperty('opacity', '0', 'important');
    anchor.style.setProperty('color', 'transparent', 'important');
  }

  private _attachSignHereOverlay(
    pageEl: HTMLElement,
    anchor: HTMLElement | null,
    pageIdx: number,
    pageNum1: number,
    tagKey: string,
    viewContainerRef: ViewContainerRef,
    signHereElements: HTMLElement[],
  ): void {
    const existing = pageEl.querySelector(
      `[data-kzn-tag-id="${tagKey}"]`,
    ) as HTMLElement | null;
    if (existing?.isConnected) {
      this._mountSignHereOnPage(existing, pageEl);
      this._prepareSignHereHost(existing);
      this._applySignHereHostDimensions(existing, pageIdx);
      const hasPlacedSig =
        !!existing.querySelector(
          '.cdk-drag img[data-type="SIGNATURE"], .cdk-drag img[signatureid]',
        ) || this.hasPlacedSignature(tagKey);
      const hasPlaceholderImg = !!existing.querySelector('img.img-sign-here');
      if (hasPlacedSig && hasPlaceholderImg) {
        this.removeSignHerePlaceholderImages(existing);
        existing.classList.add('sign-here-filled');
      } else if (!hasPlacedSig && !hasPlaceholderImg) {
        if (this.hasPlacedSignature(tagKey)) {
          this._restoreSignatureIfRegistered(existing, pageIdx, viewContainerRef, tagKey);
        } else {
          this.renderSignHereElement(existing);
        }
      }
      this._restoreSignatureIfRegistered(existing, pageIdx, viewContainerRef, tagKey);
      signHereElements.push(existing);
      return;
    }

    const host = document.createElement('div');
    host.setAttribute('data-kzn-tag-id', tagKey);
    host.setAttribute('page', String(pageIdx));
    host.classList.add('sign-here-standard');

    if (anchor && !anchor.classList.contains('box-sign-here')) {
      const anchorRect = anchor.getBoundingClientRect();
      if (anchorRect.width >= 0 && pageEl.getBoundingClientRect().width > 0) {
        const pos = this._pageRelativePercentFromRect(pageEl, anchorRect);
        host.style.left = pos.left;
        host.style.top = pos.top;
      } else {
        host.style.left = '8%';
        host.style.top = '8%';
      }
      this._clearKeywordAnchorText(anchor);
    } else {
      const kwCount = pageEl.querySelectorAll(`[data-kzn-tag-id^="kw-${pageNum1}-"]`).length;
      host.style.left = `${8 + kwCount * 2}%`;
      host.style.top = '8%';
    }

    host.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
    this._prepareSignHereHost(host);
    this._applySignHereHostDimensions(host, pageIdx);
    this._mountSignHereOnPage(host, pageEl);

    if (this.hasPlacedSignature(tagKey)) {
      this._restoreSignatureIfRegistered(host, pageIdx, viewContainerRef, tagKey);
      signHereElements.push(host);
    } else {
      signHereElements.push(this.renderSignHereElement(host));
      this._restoreSignatureIfRegistered(host, pageIdx, viewContainerRef, tagKey);
    }
  }

  generateSignHereElements(signatureTag: string, order: string, viewContainerRef: ViewContainerRef, role: EnumSignerRoleType = EnumSignerRoleType.SIGNER, alternateSignatureTag?: string): {
    signHereElements: Array<HTMLElement>;
    commentHereElements: Array<HTMLElement>;
  } {
    const signHereElements: Array<HTMLElement> = [];
    const commentHereElements: Array<HTMLElement> = [];
    const isSignerRole = this._isSignerRole(role);
    const pageNodes = document.querySelectorAll('.pdfViewer .page');

    let dynamicTags: Array<keyof typeof EnumSignTagTypes> = Object.keys(EnumSignTagTypes) as Array<keyof typeof EnumSignTagTypes>;
    dynamicTags = dynamicTags.filter((tag) => EnumSignTagTypes[tag] !== EnumSignTagTypes.COMMENT && EnumSignTagTypes[tag] !== EnumSignTagTypes.SIGNATURE);

    for (let pi = 0; pi < pageNodes.length; pi++) {
      const pageEl = this._getSignHerePageMount(pageNodes[pi] as HTMLElement);
      if (!pageEl) continue;
      const pageNum1 = parseInt(pageEl.getAttribute('data-page-number') || `${pi + 1}`, 10) || (pi + 1);
      const pageIdx = pageNum1 - 1;
      const textLayerEl = pageEl.querySelector('.textLayer') as HTMLElement | null;

      if (this.signTags.length) {
        this.signTags.forEach((pageTagRaw, index) => {
          const pageTag = SignService.normalizeSignTag(pageTagRaw as unknown as Record<string, unknown>);
          this.signTags[index] = pageTag;
          if (pageTag.Page !== pageNum1) return;

          const tagKey = `pt-${index}`;
          const existing = this._resolveSignTagHost(pageEl, tagKey);
          if (existing && existing.isConnected) {
            this._mountSignHereOnPage(existing, pageEl);
            if (pageTag.Type === EnumSignTagTypes.SIGNATURE) {
              if (!isSignerRole) {
                this.removeSignHerePlaceholderImages(existing);
                if (
                  !existing.querySelector(
                    '.cdk-drag img[data-type="SIGNATURE"], .cdk-drag img[signatureid]',
                  ) &&
                  !this.hasPlacedSignature(tagKey)
                ) {
                  this._hideUnsignedSignHereHost(existing);
                }
                return;
              }
              const hasPlacedSig =
                !!existing.querySelector(
                  '.cdk-drag img[data-type="SIGNATURE"], .cdk-drag img[signatureid]',
                ) || this.hasPlacedSignature(tagKey);
              const hasPlaceholderImg = !!existing.querySelector('img.img-sign-here');
              if (hasPlacedSig && hasPlaceholderImg) {
                this.removeSignHerePlaceholderImages(existing);
                existing.classList.add('sign-here-filled');
              } else if (!hasPlacedSig && !hasPlaceholderImg) {
                if (this.hasPlacedSignature(tagKey)) {
                  this._restoreSignatureIfRegistered(existing, pageTag.Page - 1, viewContainerRef, tagKey);
                } else {
                  this.renderSignHereElement(existing);
                }
              } else if (hasPlacedSig && !existing.querySelector('.cdk-drag')) {
                this._restoreSignatureIfRegistered(existing, pageTag.Page - 1, viewContainerRef, tagKey);
              }
              signHereElements.push(existing);
              this._prepareSignHereHost(existing);
              if (
                this._isViewModeDocument() ||
                existing.classList.contains('sign-here-standard')
              ) {
                this._applySignHereHostDimensions(existing, pageTag.Page - 1);
              }
            } else if (pageTag.Type === EnumSignTagTypes.COMMENT) {
              if (!existing.querySelector('.cdk-drag')) {
                this._applyCommentTagHostDimensions(existing);
                this.renderCommentElement(existing, pageIdx, viewContainerRef, true, 'sign-tag-comment-container');
              } else if (this._isViewModeDocument()) {
                this._applyCommentTagHostDimensions(existing);
              }
              this._restoreSignatureIfRegistered(existing, pageTag.Page - 1, viewContainerRef, tagKey);
            } else {
              if (this._shouldSkipInteractiveCustomTagHost(pageTag, isSignerRole)) {
                this._hideTagHostForReviewDisplay(existing);
                return;
              }
              this._ensureSignModeCustomTagHost(
                existing,
                pageTag,
                pageIdx,
                viewContainerRef,
                tagKey,
                isSignerRole,
              );
            }
            return;
          }

          if (this._shouldSkipInteractiveCustomTagHost(pageTag, isSignerRole)) {
            return;
          }

          const element = document.createElement('div');
          element.setAttribute('data-kzn-tag-id', tagKey);
          element.style.position = 'absolute';
          element.style.left = pageTag.XLocation + '%';
          element.style.top = pageTag.YLocation + '%';
          if (!this._isMobileSignView()) {
            element.style.width = pageTag.Width + '%';
            element.style.height = pageTag.Height + '%';
          }
          this._mountSignHereOnPage(element, pageEl);

          switch (pageTag.Type) {
            case EnumSignTagTypes.SIGNATURE:
              if (!isSignerRole) {
                element.remove();
                break;
              }
              this._applySignHereHostDimensions(element, pageTag.Page - 1);
              this._prepareSignHereHost(element);
              const signElement = this.renderSignHereElement(element);
              element.setAttribute('page', (pageTag.Page - 1).toString());
              signHereElements.push(signElement);
              this._restoreSignatureIfRegistered(element, pageTag.Page - 1, viewContainerRef, tagKey);
              break;
            case EnumSignTagTypes.COMMENT:
              this._applyCommentTagHostDimensions(element);
              this.renderCommentElement(element, pageIdx, viewContainerRef, true, 'sign-tag-comment-container');
              this._restoreSignatureIfRegistered(element, pageIdx, viewContainerRef, tagKey);
              break;
            case EnumSignTagTypes.DATE:
            case EnumSignTagTypes.CHECKBOX:
            case EnumSignTagTypes.INITIALS:
            case EnumSignTagTypes.STAMP:
            case EnumSignTagTypes.TEXT:
              if (this._shouldSkipInteractiveCustomTagHost(pageTag, isSignerRole)) {
                element.remove();
                break;
              }
              element.className = (element.classList + ' sign-tag-element-container').trim();
              element.setAttribute('page', String(pageIdx));
              element.setAttribute('data-tag-type', String(pageTag.Type));
              this.renderTagElement(
                element,
                pageTag.Type === EnumSignTagTypes.CHECKBOX && !pageTag.Text ? 'Checkbox' : pageTag.Text,
                pageTag.Type,
              );
              this._applyCustomTagHostDimensions(element, pageTag.Type, pageTag);
              this._restoreSignatureIfRegistered(element, pageIdx, viewContainerRef, tagKey);
              break;
          }

          this.signTags[index]['IsRendered'] = true;
        });
      }

      let signatureKeywordCount = 0;
      let commentKeywordCount = 0;

      pageEl.querySelectorAll('.box-sign-here.sign[data-kzn-tag-id]').forEach((node) => {
        const host = node as HTMLElement;
        if (host.closest('.textLayer')) return;
        const tagId = host.getAttribute('data-kzn-tag-id') || '';
        if (tagId.startsWith('kw-')) {
          const occ = parseInt(tagId.split('-').pop() || '0', 10);
          if (!Number.isNaN(occ) && occ >= signatureKeywordCount) {
            signatureKeywordCount = occ + 1;
          }
        } else if (tagId.startsWith('kc-')) {
          const occ = parseInt(tagId.split('-').pop() || '0', 10);
          if (!Number.isNaN(occ) && occ >= commentKeywordCount) {
            commentKeywordCount = occ + 1;
          }
        }
        this._syncExistingSignHereHost(host, pageIdx, viewContainerRef, signHereElements, isSignerRole);
      });

      if (textLayerEl) {
      const textElements = textLayerEl.children;
      for (let j = 0; j < textElements.length; j++) {
        const tj = textElements[j] as HTMLElement;

        if (
          tj.classList.contains('sign-here-standard') ||
          (tj.classList.contains('box-sign-here') && tj.classList.contains('sign')) ||
          (tj.classList.contains('box-sign-here') &&
            !tj.classList.contains('sign-tag-comment-container') &&
            !tj.classList.contains('comment-placeholder-container') &&
            !!tj.querySelector('.cdk-drag img[data-type="SIGNATURE"], .cdk-drag img[signatureid]'))
        ) {
          this._mountSignHereOnPage(tj, pageEl);
          const existingTagId = tj.getAttribute('data-kzn-tag-id') || '';
          if (existingTagId.startsWith('kw-')) signatureKeywordCount++;
          else if (existingTagId.startsWith('kc-')) commentKeywordCount++;
          this._syncExistingSignHereHost(tj, pageIdx, viewContainerRef, signHereElements, isSignerRole);
          continue;
        }

        if (
          tj.textContent
            ?.toLowerCase()
            .includes(`${Constants.COMMENT_TAG_PREFIX}${order}`.toLowerCase())
        ) {
          tj.setAttribute('page', pageIdx.toString());
          tj.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
          tj.style.height = Constants.DEFAULT_SIGN_IMAGE_HEIGHT;
          const tagKey = `kc-${pageNum1}-${commentKeywordCount++}`;
          tj.setAttribute('data-kzn-tag-id', tagKey);
          this.renderCommentElement(tj, pageIdx, viewContainerRef, true);
          this._restoreSignatureIfRegistered(tj, pageIdx, viewContainerRef, tagKey);
        } else if (
          role.toLowerCase() === EnumSignerRoleType.SIGNER.toLowerCase() &&
          (
            tj.textContent?.toLowerCase().includes(signatureTag.toLowerCase()) ||
            (alternateSignatureTag &&
              tj.textContent?.toLowerCase().includes(alternateSignatureTag.toLowerCase()))
          )
        ) {
          const pendingTagKey = `kw-${pageNum1}-${signatureKeywordCount}`;
          if (pageEl.querySelector(`[data-kzn-tag-id="${pendingTagKey}"]`)) {
            signatureKeywordCount++;
            continue;
          }
          const tagKey = `kw-${pageNum1}-${signatureKeywordCount++}`;
          this._attachSignHereOverlay(
            pageEl,
            tj,
            pageIdx,
            pageNum1,
            tagKey,
            viewContainerRef,
            signHereElements,
          );
        }

        if (order && role.toLowerCase() === EnumSignerRoleType.SIGNER.toLowerCase()) {
          dynamicTags?.forEach(tag => {
            if (
              textElements[j].textContent
                ?.toLowerCase()
                .includes(`${Constants.SIGNATURE_TAG_PREFIX}${tag}${order}`?.toLowerCase())
            ) {
              const element = textElements[j] as HTMLElement;
              element.innerHTML = '';
              element.innerText = '';
              const customTagElement = this.customSignTags.find(customTag => customTag.type === EnumSignTagTypes[tag]);
              element.style.width = customTagElement?.dimensions.width || '';
              element.style.height = customTagElement?.dimensions.height || '';
              element.className = 'sign-tag-element-container';
              this.renderTagElement(element, EnumSignTagTypes[tag] === EnumSignTagTypes.CHECKBOX ? '' : EnumSignTagTypes[tag], EnumSignTagTypes[tag] as EnumSignTagTypes, true, () => element.remove());
            }
          });
        }
      }

      if (
        textLayerEl &&
        role.toLowerCase() === EnumSignerRoleType.SIGNER.toLowerCase() &&
        signatureTag
      ) {
        const layerText = textLayerEl.textContent?.toLowerCase() || '';
        const sigLower = signatureTag.toLowerCase();
        const altLower = alternateSignatureTag?.toLowerCase() ?? '';
        const countNeedle = (needle: string): number => {
          if (!needle) return 0;
          let count = 0;
          let pos = 0;
          while ((pos = layerText.indexOf(needle, pos)) !== -1) {
            count++;
            pos += needle.length;
          }
          return count;
        };
        let expectedTotal = countNeedle(sigLower);
        if (altLower && altLower !== sigLower) {
          expectedTotal += countNeedle(altLower);
        }

        while (signatureKeywordCount < expectedTotal) {
          const pendingKey = `kw-${pageNum1}-${signatureKeywordCount}`;
          if (pageEl.querySelector(`[data-kzn-tag-id="${pendingKey}"]`)) {
            signatureKeywordCount++;
            continue;
          }
          const anchor =
            this._findNthKeywordSpanHost(textLayerEl, sigLower, signatureKeywordCount) ??
            (altLower && altLower !== sigLower
              ? this._findNthKeywordSpanHost(textLayerEl, altLower, signatureKeywordCount)
              : null);
          const tagKey = `kw-${pageNum1}-${signatureKeywordCount++}`;
          this._attachSignHereOverlay(
            pageEl,
            anchor,
            pageIdx,
            pageNum1,
            tagKey,
            viewContainerRef,
            signHereElements,
          );
        }
      }
      }
    }
    if (!isSignerRole) {
      this.suppressSignHereForNonSignerRole(role);
      signHereElements.length = 0;
    }
    return {
      signHereElements: Array.from(new Set(signHereElements)),
      commentHereElements: Array.from(new Set(commentHereElements)),
    };
  }

  private _findNthKeywordSpanHost(
    textLayer: HTMLElement,
    keyword: string,
    occurrenceIndex: number,
  ): HTMLElement | null {
    const kw = keyword.toLowerCase();
    if (!kw) return null;
    const children = Array.from(textLayer.children) as HTMLElement[];
    let found = 0;
    for (let start = 0; start < children.length; start++) {
      const startEl = children[start];
      if (startEl.classList.contains('box-sign-here')) continue;
      let acc = '';
      for (let end = start; end < children.length; end++) {
        const el = children[end];
        if (el.classList.contains('box-sign-here')) break;
        acc += el.textContent || '';
        if (acc.toLowerCase().includes(kw)) {
          if (found === occurrenceIndex) return startEl;
          found++;
          break;
        }
        if (acc.length > kw.length * 4) break;
      }
    }
    return null;
  }

 
  private _isViewModeDocument(): boolean {
    return !!document.querySelector('.pdf-viewer-container.view-mode');
  }


  private _getPageTagCoordinateFrame(pageElement: HTMLElement): {
    refRect: DOMRect;
    refWidth: number;
    refHeight: number;
  } {
    const textLayer = pageElement.querySelector('.textLayer') as HTMLElement | null;
    const canvas = pageElement.querySelector('canvas') as HTMLCanvasElement | null;
    const refEl = textLayer ?? canvas ?? pageElement;
    const refRect = (canvas ?? textLayer ?? pageElement).getBoundingClientRect();
    return {
      refRect,
      refWidth: Math.max(refEl.clientWidth || refRect.width, 1),
      refHeight: Math.max(refEl.clientHeight || refRect.height, 1),
    };
  }

  private _formatTagPercent(value: number): number {
    return parseFloat(value.toFixed(14));
  }

  private _resolveSignTagPositionRect(cdkDragEl: HTMLElement): HTMLElement {
    const host =
      (cdkDragEl.closest('.box-sign-here:not(.comment-placeholder-container)') as HTMLElement | null) ??
      (cdkDragEl.closest('.sign-tag-element-container') as HTMLElement | null) ??
      (cdkDragEl.closest('.sign-tag-comment-container') as HTMLElement | null);

    if (!host || host === cdkDragEl) return cdkDragEl;

    const hostRect = host.getBoundingClientRect();
    const dragRect = cdkDragEl.getBoundingClientRect();
    const dx = Math.abs(hostRect.left - dragRect.left);
    const dy = Math.abs(hostRect.top - dragRect.top);
    if (dx < 1 && dy < 1) return host;

    return cdkDragEl;
  }

  private _isSignHereHostedDrag(cdkDragEl: Element): boolean {
    return !!(cdkDragEl as HTMLElement).closest(
      '.box-sign-here:not(.sign-tag-comment-container):not(.comment-placeholder-container)'
    );
  }

  private _isCustomTagDrag(cdkDragEl: Element): boolean {
    return !!(cdkDragEl as HTMLElement).closest('.sign-tag-element-container');
  }

  private _getCanonicalCustomTagDimensionsPx(tagType: string): { width: number; height: number } | null {
    switch (tagType) {
      case EnumSignTagTypes.TEXT:
      case EnumSignTagTypes.INITIALS:
        return {
          width: Constants.CANONICAL_TAG_FIELD_WIDTH_PX,
          height: Constants.CANONICAL_TAG_FIELD_HEIGHT_PX,
        };
      case EnumSignTagTypes.DATE:
        return {
          width: Constants.CANONICAL_TAG_DATE_WIDTH_PX,
          height: Constants.CANONICAL_TAG_FIELD_HEIGHT_PX,
        };
      case EnumSignTagTypes.STAMP:
        return {
          width: Constants.CANONICAL_TAG_FIELD_WIDTH_PX,
          height: Constants.CANONICAL_TAG_STAMP_HEIGHT_PX,
        };
      case EnumSignTagTypes.CHECKBOX:
        return {
          width: Constants.CANONICAL_TAG_CHECKBOX_WIDTH_PX,
          height: Constants.CANONICAL_TAG_CHECKBOX_HEIGHT_PX,
        };
      default:
        return null;
    }
  }

  private _getCanonicalCustomTagSizePercents(
    tagType: string,
    pageWidth: number,
    pageHeight: number
  ): { width: number; height: number } | null {
    const dims = this._getCanonicalCustomTagDimensionsPx(tagType);
    if (!dims) return null;
    const safeW = Math.max(pageWidth, 1);
    const safeH = Math.max(pageHeight, 1);
    return {
      width: (dims.width / safeW) * 100,
      height: (dims.height / safeH) * 100,
    };
  }

  private _applyCanonicalCustomTagDisplaySize(
    host: HTMLElement,
    tagType: string | EnumSignTagTypes
  ): void {
    const dims = this._getCanonicalCustomTagDimensionsPx(String(tagType));
    if (!dims) return;
    host.classList.add('sign-tag-element-container');
    host.setAttribute('data-tag-type', String(tagType));
    host.style.width = `${dims.width}px`;
    host.style.height = `${dims.height}px`;
    host.style.maxWidth = `${dims.width}px`;
    host.style.minWidth = `${dims.width}px`;
    host.style.maxHeight = `${dims.height}px`;
    host.style.removeProperty('min-height');
  }

  private _isMobileSignView(): boolean {
    return (
      !this._isViewModeDocument() &&
      typeof window !== 'undefined' &&
      window.innerWidth <= 768
    );
  }

  private _resolveMobileSignModeCustomTagWidth(
    tagType: string,
    pageTag?: ISignTag,
    host?: HTMLElement,
  ): number {
    const normalized = this.normalizeUploadSignatureType(tagType);
    if (normalized === 'TEXT' || normalized === 'INITIALS' || normalized === 'DATE') {
      const customLabel = pageTag?.Text?.trim();
      const labelForMeasure =
        customLabel || this._resolveSelfSignFieldLabel(normalized.toLowerCase());
      let valueText = '';
      if (host) {
        const input = host.querySelector('input') as HTMLInputElement | null;
        valueText = (input?.value ?? input?.getAttribute('data-value') ?? '').trim();
      }
      const padX = 20;
      const labelW = this._measureSelfSignInlineTextWidth(labelForMeasure, 11);
      let width = Math.max(
        Constants.CANONICAL_TAG_LABELED_FIELD_MIN_WIDTH_PX,
        Math.ceil(labelW + padX),
      );
      if (valueText) {
        const textW = this._measureSelfSignInlineTextWidth(valueText, 14);
        width = Math.max(width, Math.ceil(textW + padX));
      }
      width += Constants.CANONICAL_TAG_LABELED_FIELD_WIDTH_EXTRA_PX;
      if (normalized === 'DATE') {
        width += 28;
        width = Math.max(width, Constants.CANONICAL_TAG_DATE_WIDTH_PX);
      }
      return width;
    }
    if (normalized === 'CHECKBOX') {
      const label = (pageTag?.Text ?? 'Check to confirm').trim();
      return Math.max(
        Constants.CANONICAL_TAG_CHECKBOX_FIELD_WIDTH_PX,
        this._resolveLabeledTagCaptureWidth('text', label),
      );
    }
    const dims = this._getCanonicalCustomTagDimensionsPx(normalized);
    return dims?.width ?? Constants.CANONICAL_TAG_FIELD_WIDTH_PX;
  }

  private _applyMobileSignModeCustomTagDisplaySize(
    host: HTMLElement,
    tagType: EnumSignTagTypes | string,
    pageTag?: ISignTag,
  ): void {
    if (host.getAttribute('data-kzn-user-sized') === 'true') {
      host.style.removeProperty('max-width');
      host.style.removeProperty('max-height');
      return;
    }

    const type = String(tagType);
    host.classList.add('sign-tag-element-container');
    host.setAttribute('data-tag-type', type);

    const labeledTypes = new Set<string>([
      EnumSignTagTypes.TEXT,
      EnumSignTagTypes.DATE,
      EnumSignTagTypes.INITIALS,
    ]);

    if (labeledTypes.has(type)) {
      const widthPx = this._resolveMobileSignModeCustomTagWidth(type, pageTag, host);
      const heightPx = Constants.CANONICAL_TAG_FIELD_HEIGHT_PX;
      host.style.width = `${widthPx}px`;
      host.style.height = `${heightPx}px`;
      host.style.minWidth = `${Constants.CANONICAL_TAG_LABELED_FIELD_MIN_WIDTH_PX}px`;
      host.style.minHeight = `${Constants.CANONICAL_TAG_FIELD_HEIGHT_PX}px`;
      host.style.removeProperty('max-width');
      host.style.removeProperty('max-height');
      return;
    }

    const dims = this._getCanonicalCustomTagDimensionsPx(type);
    if (!dims) return;
    host.style.width = `${dims.width}px`;
    host.style.height = `${dims.height}px`;
    host.style.minWidth = `${dims.width}px`;
    host.style.minHeight = `${dims.height}px`;
    host.style.removeProperty('max-width');
    host.style.removeProperty('max-height');
  }

  private _applyCustomTagHostDimensions(
    host: HTMLElement,
    tagType: EnumSignTagTypes | string,
    pageTag?: ISignTag,
  ): void {
    if (this._isViewModeDocument()) {
      this._applyCanonicalCustomTagDisplaySize(host, tagType);
      return;
    }
    if (this._isMobileSignView()) {
      this._applyMobileSignModeCustomTagDisplaySize(host, tagType, pageTag);
    }
  }

  private _applyCommentTagHostDimensions(host: HTMLElement): void {
    if (!this._isViewModeDocument()) return;
    host.style.width = `${Constants.DEFAULT_SIGN_COMMENT_WIDTH}px`;
    host.style.height = `${Constants.DEFAULT_SIGN_COMMENT_HEIGHT}px`;
    host.style.maxWidth = `${Constants.DEFAULT_SIGN_COMMENT_WIDTH}px`;
    host.style.minWidth = `${Constants.DEFAULT_SIGN_COMMENT_WIDTH}px`;
    host.style.maxHeight = `${Constants.DEFAULT_SIGN_COMMENT_HEIGHT}px`;
  }

  /** Submit API: always 19.5% + aspect — never inflate % on narrow mobile pages. */
  private _getCanonicalSignHereSizePercents(
    pageWidth: number,
    pageHeight: number
  ): { width: number; height: number } {
    const safeW = Math.max(pageWidth, 1);
    const safeH = Math.max(pageHeight, 1);
    const widthPercent = Constants.CANONICAL_SIGN_WIDTH_PERCENT;
    const widthPx = (widthPercent / 100) * safeW;
    const heightPx = widthPx * Constants.CANONICAL_SIGN_ASPECT;
    const heightPercent = (heightPx / safeH) * 100;
    return { width: widthPercent, height: heightPercent };
  }

  private _applyCanonicalSignHereDisplaySize(
    host: HTMLElement,
    pageElement?: HTMLElement | null
  ): void {
    const page =
      pageElement ??
      (host.closest('.page') as HTMLElement | null) ??
      (document.getElementsByClassName('page')[0] as HTMLElement | undefined);
    const pageWidth = page?.clientWidth ?? 800;
    const pageHeight = page?.clientHeight ?? 1000;
    const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;

    host.classList.add('sign-here-standard');

    if (isMobileView) {
      host.style.width = Constants.MOBILE_SIGN_IMAGE_WIDTH;
      host.style.maxWidth = Constants.MOBILE_SIGN_IMAGE_MAX_WIDTH;
      host.style.minWidth = Constants.MOBILE_SIGN_IMAGE_MIN_WIDTH;
      host.style.removeProperty('height');
      host.style.removeProperty('min-height');
      return;
    }

    const fixedWidthPx = Constants.VIEW_MODE_DESKTOP_SIGN_MAX_WIDTH_PX;
    const fixedHeightPx = fixedWidthPx * Constants.CANONICAL_SIGN_ASPECT;
    host.style.width = `${fixedWidthPx}px`;
    host.style.height = `${fixedHeightPx}px`;
    host.style.maxWidth = `${fixedWidthPx}px`;
    host.style.minWidth = `${fixedWidthPx}px`;
    host.style.maxHeight = `${fixedHeightPx}px`;
    host.style.removeProperty('min-height');
  }

  /** Sign-mode mobile = larger box; view-mode = canonical desktop footprint. */
  private _applySignHereHostDimensions(host: HTMLElement, pageIndex?: number): void {
    if (this._isViewModeDocument()) {
      const page = document.getElementsByClassName('page')[pageIndex ?? 0] as HTMLElement | undefined;
      this._applyCanonicalSignHereDisplaySize(host, page);
      return;
    }
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this._applyMobileSignatureTagDimensions(host);
      return;
    }
    host.classList.add('sign-here-standard');
    if (!host.style.width || host.style.width === 'auto') {
      host.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
    }
    host.style.minWidth = `${Constants.CANONICAL_SIGN_MIN_WIDTH_PX}px`;
    host.style.maxWidth = '150px';
    host.style.removeProperty('height');
    host.style.removeProperty('min-height');
  }

  private _applyMobileSignatureTagDimensions(host: HTMLElement): void {
    if (typeof window === 'undefined' || window.innerWidth > 768) return;
    if (this._isViewModeDocument()) return;
    host.classList.add('sign-here-standard');
    host.style.width = Constants.MOBILE_SIGN_IMAGE_WIDTH;
    host.style.maxWidth = Constants.MOBILE_SIGN_IMAGE_MAX_WIDTH;
    host.style.minWidth = Constants.MOBILE_SIGN_IMAGE_MIN_WIDTH;
    host.style.removeProperty('height');
    host.style.removeProperty('min-height');
  }


  private _restoreSignatureIfRegistered(
    container: HTMLElement,
    pageIndex: number,
    viewContainerRef: ViewContainerRef,
    tagId: string,
  ): void {
    if (!container || !tagId || !viewContainerRef) return;
    const record = this._placedSignatures.get(tagId);
    if (!record) return;
    if (container.querySelector('.cdk-drag')) return;

   
    const wasStandard = container.classList.contains('sign-here-standard');
    const wasCommentContainer = container.classList.contains('sign-tag-comment-container');
    const isCommentSpan = !!record.isCommentOnly || wasCommentContainer;
    const preserveStandardAspect = !isCommentSpan && wasStandard;
    const isMobileSign = typeof window !== 'undefined' && window.innerWidth <= 768;
    const isViewMode = this._isViewModeDocument();

    container.innerHTML = '';
    if (isCommentSpan) {
      container.className = 'box-sign-here sign-tag-comment-container';
    } else if (isMobileSign && !isViewMode) {
      container.className = 'box-sign-here sign-here-standard';
    } else {
      container.className = preserveStandardAspect
        ? 'box-sign-here sign-here-standard'
        : 'box-sign-here';
    }
    // Re-stamp the tag id (className reset above wipes any non-class
    // attributes only when we explicitly clear them, but stay defensive).
    container.setAttribute('data-kzn-tag-id', tagId);

    if (isCommentSpan) {
      container.style.width = `${Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE}%`;
      container.style.height = `${Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE}%`;
    } else if (isViewMode) {
      this._applyCanonicalSignHereDisplaySize(container);
    } else if (isMobileSign) {
      container.style.width = Constants.MOBILE_SIGN_IMAGE_WIDTH;
      container.style.maxWidth = Constants.MOBILE_SIGN_IMAGE_MAX_WIDTH;
      container.style.minWidth = Constants.MOBILE_SIGN_IMAGE_MIN_WIDTH;
      container.style.removeProperty('height');
      container.style.removeProperty('min-height');
    } else if (preserveStandardAspect) {
      if (!container.style.width || container.style.width === 'auto') {
        container.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
      }
      container.style.minWidth = '120px';
      container.style.removeProperty('height');
      container.style.removeProperty('min-height');
    } else {
      if (!container.style.width || container.style.width === 'auto') {
        container.style.width = Constants.DEFAULT_SIGN_IMAGE_WIDTH;
      }
      if (!container.style.height || container.style.height === 'auto') {
        container.style.height = Constants.DEFAULT_SIGN_IMAGE_HEIGHT;
      }
      container.style.minWidth = '120px';
      container.style.minHeight = '64px';
    }

    this.addSignImage(
      record.imageBase64,
      '0',
      '0',
      pageIndex,
      viewContainerRef,
      record.isDraggable !== false,
      !!record.isCommentOnly,
      record.signComment,
      container,
      record.comment,
      record.signatureId,
      true,
      !!record.disableDelete,
    );
    this.removeSignHerePlaceholderImages(container);
    container.classList.add('sign-here-filled');
    void this.refreshPlacedSignatureSnapshot(tagId, viewContainerRef);
  }

  private mapSignerSignatureResponse(response: { Data?: IGetSignerSignature[] }): IGetSignerSignature[] {
    return (response?.Data ?? []).map((sign: IGetSignerSignature) => ({
      ...sign,
      SignImagePath: sign.SignImagePath?.startsWith('data:')
        ? sign.SignImagePath
        : 'data:image;base64,' + sign.SignImagePath,
    }));
  }

  getSignerSignatures(signRequestId: string, signerId: string): void {
    const params = {
      SignRequestId: signRequestId,
      SignerId: signerId,
    };

    this.isLoadingSignerSignatures = true;
    this.signerSignatures = [];

    this._httpService.get(
      this._httpService.createEndpoint(
        Constants.API_URI_SIGN_GET_SIGNER_SIGNATURE,
        undefined,
        params
      )
    ).subscribe({
      next: (response: unknown) => {
        this.isLoadingSignerSignatures = false;
        this.signerSignatures = this.mapSignerSignatureResponse(response as { Data?: IGetSignerSignature[] });
      },
      error: (errorResponse) => {
        this.isLoadingSignerSignatures = false;
        this._httpService.showHttpError(errorResponse);
      },
    });
  }

  /**
   * Loads saved signatures for the logged-in portal user (self-sign before a request exists).
   */
  getLoggedInUserSignatures(
    loginEmail: string,
    onLoaded?: (signatures: IGetSignerSignature[]) => void,
  ): void {
    const email = (loginEmail ?? '').trim();
    if (!email) {
      this.signerSignatures = [];
      onLoaded?.([]);
      return;
    }

    this.isLoadingSignerSignatures = true;
    this.signerSignatures = [];

    this._httpService.get(
      this._httpService.createEndpoint(
        Constants.API_URI_SIGN_GET_SIGNER_SIGNATURE,
        undefined,
        { SignRequestId: '', SignerId: '', LoginId: email, Email: email },
      ),
    ).subscribe({
      next: (response: unknown) => {
        this.isLoadingSignerSignatures = false;
        this.signerSignatures = this.mapSignerSignatureResponse(response as { Data?: IGetSignerSignature[] });
        onLoaded?.(this.signerSignatures);
      },
      error: () => {
        this._httpService.get(
          this._httpService.createEndpoint(
            Constants.API_URI_SIGN_GET_SIGNER_SIGNATURE,
            undefined,
            { SignRequestId: '', SignerId: email },
          ),
        ).subscribe({
          next: (fallbackResponse: unknown) => {
            this.isLoadingSignerSignatures = false;
            this.signerSignatures = this.mapSignerSignatureResponse(
              fallbackResponse as { Data?: IGetSignerSignature[] },
            );
            onLoaded?.(this.signerSignatures);
          },
          error: () => {
            this.isLoadingSignerSignatures = false;
            this.signerSignatures = [];
            onLoaded?.([]);
          },
        });
      },
    });
  }
}



